import { useRef, useEffect, useLayoutEffect, useState, useCallback, useMemo, forwardRef, useImperativeHandle, memo } from 'react';
import Editor from '@monaco-editor/react';

// Note: Monaco CDN version is pinned in `src/main.tsx` so the loader config runs
// before any @monaco-editor/react consumer in the app can trigger loader.init.
import { useAppStore } from '../../../../store/appStore';
import {
  isAutoSuggestEnabled,
  isCompletionEnabled,
  isHoverAssistEnabled,
  isLiveDiagnosticsEnabled,
  isSemanticCompletionEnabled,
} from '@/features/intellisense/settings/intellisenseGates';
import { getLanguageServiceProvider } from '../../../connections/services/language-services/languageServiceRegistry';
import type {
  LanguageServiceProvider,
  LanguageServiceConnectionInfo,
  LanguageServiceDiagnostic,
} from '../../../connections/services/language-services/languageServiceProvider';
import {
  initializeConnectionSyncManager,
  registerDocumentForConnection,
  unregisterDocumentForConnection,
} from '../../../connections/services/connectionSyncManager';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import {
  getStatementTextBeforeCursor,
} from '../utils/sqlCompletionSorter';
import type * as monacoNs from 'monaco-editor';
import type { editor as monacoEditor } from 'monaco-editor';
import type { Monaco, IStandaloneCodeEditor, SqlMonacoHandle, SqlMonacoProps } from '../utils/sqlMonacoTypes';
import { getConnectionIdFromDocumentUri } from '../utils/sqlMonacoTypes';
import { executeEditsWithUndoStop } from '../utils/executeEditsWithUndoStop';
import { formatSql } from '../utils/sqlFormatter';
import { isModelTooLargeToRead, readModelTextSafe } from '../utils/monacoModelText';
import { installOversizedPasteGuard } from '../utils/editorPasteGuard';
import { toast } from '../../../../components/ui/toaster';
import { getSqlSnippetCompletions } from '../utils/sqlSnippets';
import { mergeCompletionSuggestions } from '../utils/sqlCompletionMerge';
import { ensureJamMonacoThemes } from '../../../../core/theme/monacoTheme';
import { registerSqlLanguage } from '../utils/sqlMonarchTokenizer';
import { registerPlSqlLanguage } from '../utils/plsqlLanguage';
import { ensureKustoLanguageLoaded } from '../utils/kustoLanguage';
import { buildHoverContents } from '../utils/hoverContentBuilder';
import { computeInsertInlayHints } from '../utils/insertInlayHintsProvider';
import { computeInsertSignatureHelp } from '../utils/insertSignatureHelp';
import { computeFunctionSignatureHelp } from '../utils/functionSignatureHelp';
import { createSelectStarHoverProvider } from '../utils/selectStarHoverProvider';
import type { DatabaseEngineId } from '@/core/engines';
import { MONO_FONT_STACK } from '@/core/theme/fonts';
import {
  notifyEditorBlur,
  notifyEditorFocus,
  recordKeystroke,
} from '@/core/analytics/editorInputLatency';
import { useSqlSnippetsStore } from '@/features/sql-snippets/store/sqlSnippetsStore';
import { normalizeSnippetEngineId } from '@/features/sql-snippets/utils/sqlSnippetTemplateUtils';
import { provideCompletionItems as runOrchestrator } from '@/features/intellisense/orchestrator/orchestrator';
import { isInsideRoutineBodyBlock } from '@/features/intellisense/context/insideRoutineBody';
import { getResolverRegistry } from '@/features/intellisense/orchestrator/resolverRegistry';
import { computeVersionGatingHint } from '@/features/intellisense/orchestrator/computeVersionGatingHint';
import { startProviderCost } from '@/features/intellisense/telemetry/providerCost';
import { yieldToEventLoop } from '@/features/connections/services/language-services/introspectionPaging';
import {
  wireCompletionTelemetry,
  type CompletionTelemetryHandle,
} from '@/features/intellisense/telemetry/completionTelemetryWiring';
import { recordAcceptedCompletion } from '@/features/intellisense/ranking/acceptanceHistory';
import { applyDiagnosticsSuppressionAndSet } from '@/features/intellisense/diagnostics/applyDiagnosticsSuppression';
import { toMarkerCode } from '@/features/intellisense/diagnostics/markerCode';
import { collectTypeCheckMarkers } from '@/features/intellisense/diagnostics/typeCheck';
import { buildSyntaxMarkersFromParseErrors } from '@/features/intellisense/diagnostics/syntax/collectSyntaxMarkers';
import { getWorkerSyntaxParseErrors } from '@/features/intellisense/diagnostics/syntax/workerSyntaxErrorSource';
import { createLiveSyntaxPassRunner, runTreeSitterSyntaxPass } from '@/features/intellisense/diagnostics/syntax/treeSitterSyntaxLivePass';
import type { TreeSitterSyntaxEngineId } from '@/features/intellisense/diagnostics/syntax/collectTreeSitterSyntaxMarkers';
import { dispatchProceduralWarmUpFromModel } from '@/features/intellisense/resolvers/proceduralWarmUp';
import { resolveSemanticHover } from '@/features/intellisense/hover/resolveSemanticHover';
import { databaseEngineIdToSqlEngine } from '@/features/intellisense/versionGating/engineIdMapping';
import { subscribe as subscribeSchemaBus, getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { getMssqlHoverInfo } from '@/features/connections/services/language-services/mssqlHover';
import { makeWarmSuggestRetrigger } from '@/features/intellisense/schemaBus/retriggerSuggestOnWarm';
import { isSuggestWidgetVisible, rerunSuggestSession } from '@/features/workspace/common/utils/suggestWidget';
import { computeScriptScopeFromBuffer } from '../utils/scriptScope';
import { attachCodeActionLightbulb } from '../utils/codeActionLightbulb';
import { createDefinitionProvider } from '@/features/navigational-features/go-to/definitionProvider';
import { installDefinitionNavigationOpener } from '@/features/navigational-features/go-to/definitionNavigationOpener';
import {
  installUnownedModelDefinitionProvider,
  registerDefinitionModelOwner,
} from '@/features/navigational-features/go-to/unownedModelDefinitionProvider';
import { createCodeActionProvider, registerNavCodeActionTrackingCommand } from '@/features/navigational-features/code-actions/codeActionProvider';
import { createReferenceProvider } from '@/features/navigational-features/find-references/referenceProvider';
import { installPeekReferenceIntegration } from '@/features/navigational-features/find-references/peekReferenceIntegration';
import { createFindReferencesInTabAction } from '@/features/navigational-features/find-references/findReferencesInTabAction';
import { createRenameProvider } from '@/features/navigational-features/rename/renameProvider';
import { createCodeActionRegistry } from '@/features/navigational-features/code-actions/codeActionRegistry';
import { addColumnToGroupByEntry } from '@/features/navigational-features/code-actions/fixes/addColumnToGroupBy';
import { addMissingJoinPredicateEntry } from '@/features/navigational-features/code-actions/fixes/addMissingJoinPredicate';
import { addMissingTableToFromEntry } from '@/features/navigational-features/code-actions/fixes/addMissingTableToFrom';
import { extractSubqueryToCteEntry } from '@/features/navigational-features/code-actions/fixes/extractSubqueryToCte';
import { fixKeywordTypoEntry } from '@/features/navigational-features/code-actions/fixes/fixKeywordTypo';
import { qualifyAmbiguousColumnEntry } from '@/features/navigational-features/code-actions/fixes/qualifyAmbiguousColumn';
import { quoteNumericLiteralEntry } from '@/features/navigational-features/code-actions/fixes/quoteNumericLiteral';
import { classifyRowLimit } from '@/features/intellisense/context/rowLimitSlot';
import { scanOptionsFor } from '@/features/intellisense/context/slotTextScan';
import {
  noteKeybindingTargetFocus,
} from '@/features/keybindings/dispatch/targetRegistry';
import { registerFormatSqlContextAction, useFormatSqlKeybinding } from '../hooks/useFormatSqlKeybinding';

// Re-export types for backward compatibility
export type { Monaco, IStandaloneCodeEditor, SqlMonacoHandle, SqlMonacoProps };
export type { SqlMonacoMarker } from '../utils/sqlMonacoTypes';

// Raw LSP signature-help payload shape (the language-service provider returns
// `Promise<any>`; this is the structural subset this component reads to build
// a Monaco `SignatureHelp`). `documentation` is either a plain string or an
// LSP `MarkupContent` (`{ value }`), matching the LSP wire format.
type LspDoc = string | { value?: string };
type LspParameterInformation = { label: string | [number, number]; documentation?: LspDoc };
type LspSignatureInformation = {
  label: string;
  documentation?: LspDoc;
  parameters?: LspParameterInformation[];
};

// Post-insert analytics for snippet completions. Monaco runs a completion
// item's `command` after the snippet is inserted — the only reliable
// "accepted" signal. Registered once per app (module guard: registerCommand
// is a global registry, and every mounted SqlMonaco shares it). `snippetId`
// stays low-cardinality: built-in template ids pass through, all custom
// snippets collapse to 'custom'.
// Monaco fires onDidChangeCursorSelection for every character the selection
// endpoint crosses during a drag — dozens of events per second. Selection
// consumers (floating bars, toolbar morph, AI sync) only need the settled
// selection, so emission is coalesced with a trailing debounce and the
// payload (getValueInRange, getOffsetAt) is computed once, when it fires.
// Anything that must not observe this lag (execute paths) reads the live
// selection from the SqlMonacoHandle at action time instead.
const SELECTION_EMIT_DEBOUNCE_MS = 50;
const SNIPPET_INSERT_COMMAND_ID = 'jamsql.snippetInsertTracked';
let snippetInsertCommandRegistered = false;
function ensureSnippetInsertCommand(monaco: Monaco): void {
  if (snippetInsertCommandRegistered) return;
  snippetInsertCommandRegistered = true;
  monaco.editor.registerCommand(SNIPPET_INSERT_COMMAND_ID, (_accessor: unknown, engine: string, snippetId: string) => {
    window.electronAPI?.analytics?.track('feature:snippet_insert', { engine, snippetId }).catch(() => {});
  });
}

// Memoised, and the underlying Monaco is mounted UNCONTROLLED — the `value`
// prop is used only as the initial buffer. After mount, Monaco's model is the
// single source of truth for the editing buffer and React must NEVER re-push
// `value` back into the editor. External callers that need to overwrite the
// buffer (script generation, recent-query selection, MCP set-text) must use
// the imperative `setValue` on the SqlMonacoHandle. Keeping React out of the
// keystroke path is what makes typing VSCode-snappy.

/**
 * Replace the whole buffer with its formatted text as ONE undo step, for every
 * Format entry point (toolbar, Shift+Alt+F, context menu). `setValue` would
 * wipe the editor's undo history — typed and Visual-view edits alike.
 */
type FormattableEditor = {
  getModel(): monacoEditor.ITextModel | null;
  pushUndoStop(): boolean;
  executeEdits(source: string | null | undefined, edits: monacoEditor.IIdentifiedSingleEditOperation[]): boolean;
};

function applyFormattedSql(editor: FormattableEditor | null | undefined, formatted: string): void {
  const model = editor?.getModel();
  if (!editor || !model || formatted === readModelTextSafe(model)) return;
  executeEditsWithUndoStop(editor, 'format-sql', [{ range: model.getFullModelRange(), text: formatted }]);
}
const SqlMonacoInner = forwardRef<SqlMonacoHandle, SqlMonacoProps>(function SqlMonaco({
  value,
  onChange,
  engineId,
  connectionId,
  database,
  className = '',
  height = '100%',
  language: languageProp,
  wordWrap: wordWrapProp,
  onIntelliSenseStatusChange,
  onSelectionChange,
}, ref) {
  if (typeof window !== 'undefined' && (window as unknown as { __SQL_STUDIO_PROFILE__?: boolean }).__SQL_STUDIO_PROFILE__) {
     
    console.log('[PROFILE] SqlMonaco render');
  }
  const editorLanguage = languageProp ?? (engineId === 'kusto' ? 'kusto' : engineId === 'oracle' ? 'plsql' : 'sql');
  // 'kusto' is intentionally NOT an SQL language: monaco-kusto ships its own providers.
  const isSqlLanguage = editorLanguage === 'sql' || editorLanguage === 'plsql';
  // Kusto rides the language-service DOCUMENT lifecycle (registerDocument +
  // connectForLanguageServices) so its provider can push the cluster schema
  // into monaco-kusto's worker — but NOT the Jam SQL Monaco providers below
  // (completion/hover/signature/inlay), which monaco-kusto supplies itself.
  const usesLanguageServiceDocument = isSqlLanguage || editorLanguage === 'kusto';
  const editorRef = useRef<IStandaloneCodeEditor | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const engineIdRef = useRef<DatabaseEngineId>(engineId ?? 'mssql');
  // Engine label for typing-latency telemetry. Deliberately NOT engineIdRef:
  // that one carries the language-service fallback ('mssql' when nothing is
  // known), which mislabelled every disconnected / untitled / startup
  // keystroke as mssql. This resolves ONLY from a live connection and is
  // `undefined` otherwise, so the sample lands in the 'unknown' bucket.
  const telemetryEngine = useConnectionStore((state) =>
    connectionId
      ? state.activeConnections.find((conn) => conn.id === connectionId)?.connection?.engine
      : undefined,
  );
  // Read by the focus handler, which is installed once at mount.
  const telemetryEngineRef = useRef(telemetryEngine);
  telemetryEngineRef.current = telemetryEngine;
  // Owner token for the typing-latency focus scope — lets an unmounting
  // editor release focus without blinding a sibling that took it since.
  const inputLatencyOwnerRef = useRef<symbol | null>(null);
  const hasInputLatencyFocusRef = useRef(false);
  useEffect(
    () => () => {
      if (inputLatencyOwnerRef.current) notifyEditorBlur(inputLatencyOwnerRef.current);
    },
    [],
  );
  // The connection can land while the editor is already focused (session
  // restore, reconnect). Re-scope so post-connect typing is attributed to the
  // real engine instead of staying in the 'unknown' bucket until the next blur.
  useEffect(() => {
    if (!hasInputLatencyFocusRef.current) return;
    const editor = editorRef.current;
    if (!editor) return;
    inputLatencyOwnerRef.current = notifyEditorFocus(
      telemetryEngine,
      editor.getModel()?.getLineCount() ?? 0,
    );
  }, [telemetryEngine]);
  // Monaco's word-based suggestions scrape identifiers out of the document and
  // offer them with no idea what is legal at the caret. That is a useful
  // fallback almost everywhere — but NOT inside the row-limiting clause, where
  // the resolver deliberately answers with nothing because only an integer is
  // legal. There the scrape filled the empty widget with whatever words were on
  // screen: `Invoice`, `CustomerId`, and worst of all `ROWS`, so at
  // `OFFSET ▮50 ROWS` pressing Enter wrote `OFFSET ROWS`.
  //
  // Scoped to that clause rather than turned off globally: the fallback still
  // covers the positions the resolver does not reach (a name inside a dynamic
  // SQL string, an enum literal), and switching it off everywhere would hide
  // those gaps behind an empty widget instead of a wrong one.
  const syncWordBasedSuggestions = useCallback((editor: IStandaloneCodeEditor) => {
    const caretEditor = editor as IStandaloneCodeEditor & {
      getPosition?: () => { lineNumber: number; column: number } | null;
      updateOptions?: (options: { wordBasedSuggestions: boolean }) => void;
    };
    try {
      const model = caretEditor.getModel();
      const position = caretEditor.getPosition?.();
      if (!model || !position || !caretEditor.updateOptions) return;
      const text = model.getValue();
      const offset = model.getOffsetAt(position);
      // Bound the backward anchor search to the current statement so a `LIMIT`
      // in an earlier statement cannot claim this caret.
      const statementStart = text.lastIndexOf(';', Math.max(0, offset - 1)) + 1;
      const inRowLimitClause =
        classifyRowLimit(text, offset, statementStart, scanOptionsFor(engineIdRef.current)) !== null;
      caretEditor.updateOptions({
        wordBasedSuggestions:
          useAppStore.getState().intellisenseSettings.enabled && !inRowLimitClause,
      });
    } catch {
      // Never let a caret-tracking convenience break typing.
    }
  }, []);

  const modelPathRef = useRef(`inmemory://sql-studio/${Date.now()}-${Math.random().toString(16).slice(2)}.sql`);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const selectionDisposableRef = useRef<{ dispose: () => void } | null>(null);
  // Our own content-change subscription (replaces the `<Editor onChange>`
  // prop) and the capture-phase paste guard; both torn down on unmount.
  const contentChangeDisposableRef = useRef<{ dispose: () => void } | null>(null);
  const pasteGuardDisposeRef = useRef<(() => void) | null>(null);
  // Latest `handleEditorChange` — the Monaco listener is installed once at
  // mount and must not capture a stale closure (documentUri, onChange…).
  const handleEditorChangeRef = useRef<(text: string) => void>(() => {});
  const selectionEmitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ids for the "current statement" decoration (empty when no highlight applied).
  const currentStatementDecorationsRef = useRef<string[]>([]);
  // Tracks the latest `value` prop so handleEditorDidMount — whose callback ref
  // is captured by @monaco-editor/react at first render — can read the current
  // value when Monaco's lazy chunk finally finishes loading. Without this, an
  // imperative `setValue` issued via the handle before Monaco mounts is silently
  // dropped (editorRef.current was still null), and the editor stays on the
  // stale defaultValue captured at mount. This shows up as e.g. async
  // "Script as CREATE" generation completing during Monaco's mount window
  // leaving the "-- Generating CREATE script…" placeholder in the editor.
  const latestValueRef = useRef(value);
  latestValueRef.current = value;

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  useEffect(() => {
    return () => {
      selectionDisposableRef.current?.dispose?.();
      contentChangeDisposableRef.current?.dispose?.();
      contentChangeDisposableRef.current = null;
      pasteGuardDisposeRef.current?.();
      pasteGuardDisposeRef.current = null;
    };
  }, []);

  const createDocumentUri = (params: { connectionId?: string; database?: string }): string => {
    const unique = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    if (!params.connectionId) {
      return `file:///sql-studio/untitled/${unique}.sql`;
    }
    const databaseSegment = params.database ? encodeURIComponent(params.database) : 'no-db';
    return `file:///sql-studio/${params.connectionId}/${databaseSegment}/documents/${unique}.sql`;
  };

  // Provider selection only — the 'mssql' fallbacks below MUST stay (every
  // editor needs some language service). For anything that reports what the
  // user is actually using, see `telemetryEngine` above.
  const resolveEngineId = (): DatabaseEngineId => {
    if (engineId) return engineId;
    if (!connectionId) return 'mssql';

    const { activeConnections } = useConnectionStore.getState();
    const activeConnection = activeConnections.find((conn) => conn.id === connectionId);
    const connectionEngine = activeConnection?.connection?.engine;

    if (connectionEngine === 'mssql' || connectionEngine === 'pgsql' || connectionEngine === 'mysql' || connectionEngine === 'sqlite' || connectionEngine === 'oracle' || connectionEngine === 'kusto' || connectionEngine === 'mock') {
      return connectionEngine;
    }

    return 'mssql';
  };

  // Expose methods to parent components via ref
  useImperativeHandle(ref, () => ({
    getSelectedText: () => {
      if (!editorRef.current) return '';
      const selection = editorRef.current.getSelection();
      if (!selection || selection.isEmpty()) return '';
      const model = editorRef.current.getModel();
      if (!model) return '';
      return model.getValueInRange(selection);
    },
    getSelectionRange: () => {
      if (!editorRef.current) return null;
      const selection = editorRef.current.getSelection?.();
      if (!selection || typeof selection.isEmpty !== 'function' || selection.isEmpty()) return null;
      return {
        startLineNumber: selection.startLineNumber,
        startColumn: selection.startColumn,
        endLineNumber: selection.endLineNumber,
        endColumn: selection.endColumn,
      };
    },
    getValue: () => {
      // `undefined` before Monaco mounts (the parent's own copy of the text is
      // authoritative then — a '' here made Save write an empty file) and when
      // Monaco refuses to hand the buffer back (model past its heap-operation
      // threshold). Callers fall back or refuse; never ''.
      if (!editorRef.current) return undefined;
      return readModelTextSafe(editorRef.current.getModel()) ?? undefined;
    },
    isBufferUnreadable: () => {
      if (!editorRef.current) return false;
      return isModelTooLargeToRead(editorRef.current.getModel());
    },
    getCursorOffset: () => {
      const editor = editorRef.current;
      const model = editor?.getModel?.();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const position = (editor as any)?.getPosition?.();
      if (!model || !position || typeof model.getOffsetAt !== 'function') return null;
      try {
        return model.getOffsetAt(position);
      } catch {
        return null;
      }
    },
    getModelVersionId: () => {
      const model = editorRef.current?.getModel?.();
      if (!model || typeof model.getVersionId !== 'function') return null;
      try {
        return model.getVersionId();
      } catch {
        return null;
      }
    },
    setValue: (newValue: string) => {
      if (!editorRef.current) return false;
      editorRef.current.setValue(newValue);
      return true;
    },
    undo: () => {
      if (!editorRef.current) return false;
      editorRef.current.trigger('api', 'undo', null);
      return true;
    },
    redo: () => {
      if (!editorRef.current) return false;
      editorRef.current.trigger('api', 'redo', null);
      return true;
    },
    executeEdits: (source, edits) => {
      if (!editorRef.current) return false;
      // `edits` is the structural `{ range, text }[]` shape (see SqlMonacoHandle)
      // — assignable to Monaco's `IIdentifiedSingleEditOperation[]` at runtime;
      // the cast bridges the readonly structural type to Monaco's mutable one.
      //
      // Bracketed with pushUndoStop() on both sides (P12 gate fix): an
      // external, programmatic splice must always be its own undo unit — see
      // `executeEditsWithUndoStop.ts` and the SqlMonacoHandle.executeEdits
      // docblock for the coalescing bug this prevents.
      return executeEditsWithUndoStop(
        editorRef.current,
        source,
        edits as unknown as monacoEditor.IIdentifiedSingleEditOperation[],
      );
    },
    setSelectionRange: (range) => {
      if (!editorRef.current) return false;

      const monaco = monacoRef.current;
      try {
        if (typeof editorRef.current.setSelection !== 'function') return false;

        if (monaco?.Selection) {
          editorRef.current.setSelection(
            new monaco.Selection(
              range.startLineNumber,
              range.startColumn,
              range.endLineNumber,
              range.endColumn
            )
          );
        } else {
          editorRef.current.setSelection(range);
        }

        return true;
      } catch {
        return false;
      }
    },
    focus: () => {
      if (editorRef.current) {
        editorRef.current.focus();
        return true;
      }
      return false;
    },
    hasTextFocus: () => editorRef.current?.hasTextFocus() ?? false,
    formatSql: () => {
      if (!editorRef.current) return;
      const currentValue = readModelTextSafe(editorRef.current.getModel());
      if (currentValue === null) return;
      // `formatSql` is async because `sql-formatter` is code-split (it is only
      // ever reached from this user action) — the handle stays fire-and-forget.
      void formatSql(currentValue, engineIdRef.current).then((formatted) => {
        applyFormattedSql(editorRef.current, formatted);
      });
    },
    insertSnippet: (snippet: string) => {
      if (!editorRef.current) return false;
      // Use Monaco's SnippetController2 contribution to insert with tabstops
      // See: https://github.com/Microsoft/monaco-editor/issues/342
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const snippetController = (editorRef.current as any).getContribution('snippetController2');
      if (snippetController) {
        snippetController.insert(snippet);
        return true;
      }
      // Fallback: just set the value without tabstops
      editorRef.current.setValue(snippet.replace(/\$\{\d+:?([^}]*)\}/g, '$1').replace(/\$0/g, ''));
      return true;
    },
    setMarkers: (markers, owner = 'package-errors') => {
      const monaco = monacoRef.current;
      const model = editorRef.current?.getModel?.();
      if (!monaco || !model) return;
      const severityMap = { error: 8, warning: 4, info: 2 }; // MonacoMarkerSeverity
      monaco.editor.setModelMarkers(model, owner, markers.map((m) => ({
        startLineNumber: m.startLineNumber,
        startColumn: m.startColumn,
        endLineNumber: m.endLineNumber,
        endColumn: m.endColumn,
        message: m.message,
        severity: severityMap[m.severity] ?? 8,
      })));
    },
    revealLine: (line, column) => {
      const editor = editorRef.current;
      if (!editor) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor as any).revealLineInCenter?.(line);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor as any).setPosition?.({ lineNumber: line, column: column ?? 1 });
      editor.focus();
    },
    getSelectionPixelPosition: () => {
      const editor = editorRef.current;
      if (!editor) return null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const selection = (editor as any).getSelection?.();
      if (!selection || (typeof selection.isEmpty === 'function' && selection.isEmpty())) return null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const startPos = (editor as any).getScrolledVisiblePosition?.({
        lineNumber: selection.startLineNumber,
        column: selection.startColumn,
      });
      if (!startPos) return null;
      // Also resolve the pixel position of the LAST line of the selection so
      // callers can anchor widgets below the entire selection (not just line 1).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const endPos = (editor as any).getScrolledVisiblePosition?.({
        lineNumber: selection.endLineNumber,
        column: selection.endColumn,
      });
      const startHeight = startPos.height ?? 18;
      const endTop = endPos?.top ?? startPos.top;
      const endHeight = endPos?.height ?? startHeight;
      return {
        top: startPos.top,
        left: startPos.left,
        height: startHeight,
        bottom: endTop + endHeight,
      };
    },
    addViewZone: (afterLineNumber: number, heightInPx: number, domNode: HTMLDivElement) => {
      const editor = editorRef.current;
      if (!editor) return null;
      let zoneId: string | null = null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor as any).changeViewZones?.((accessor: any) => {
        zoneId = accessor.addZone({
          afterLineNumber,
          heightInPx,
          domNode,
          suppressMouseDown: true,
        });
      });
      return zoneId;
    },
    removeViewZone: (zoneId: string) => {
      const editor = editorRef.current;
      if (!editor) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor as any).changeViewZones?.((accessor: any) => {
        accessor.removeZone(zoneId);
      });
    },
    getSelectionEndLine: () => {
      const editor = editorRef.current;
      if (!editor) return null;
      const selection = editor.getSelection?.();
      if (!selection || (typeof selection.isEmpty === 'function' && selection.isEmpty())) return null;
      return selection.endLineNumber;
    },
    onDidChangeContent: (callback) => {
      const editor = editorRef.current;
      if (!editor) return () => undefined;
      const disposable = editor.onDidChangeModelContent((event) => {
        callback({
          changes: event.changes.map((c) => ({ rangeOffset: c.rangeOffset, rangeLength: c.rangeLength, text: c.text })),
          isFlush: event.isFlush,
        });
      });
      return () => disposable.dispose();
    },
    onDidScrollChange: (callback: () => void) => {
      const editor = editorRef.current;
      if (!editor) return () => undefined;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const disposable = (editor as any).onDidScrollChange?.(() => {
        callback();
      });
      return () => {
        disposable?.dispose?.();
      };
    },
    onDidLayoutChange: (callback: () => void) => {
      const editor = editorRef.current;
      if (!editor) return () => undefined;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const disposable = (editor as any).onDidLayoutChange?.(() => {
        callback();
      });
      return () => {
        disposable?.dispose?.();
      };
    },
    getLayoutHeight: () => {
      const editor = editorRef.current;
      if (!editor) return null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const info = (editor as any).getLayoutInfo?.();
      if (!info || typeof info.height !== 'number') return null;
      return info.height;
    },
    getPixelPositionForPosition: (lineNumber: number, column: number) => {
      const editor = editorRef.current;
      if (!editor) return null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pos = (editor as any).getScrolledVisiblePosition?.({ lineNumber, column });
      if (!pos) return null;
      return { top: pos.top, left: pos.left, height: pos.height ?? 18 };
    },
    getEditorRect: () => {
      const editor = editorRef.current;
      if (!editor) return null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dom: HTMLElement | undefined = (editor as any).getDomNode?.()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ?? (editor as any).getContainerDomNode?.();
      if (!dom || typeof dom.getBoundingClientRect !== 'function') return null;
      const rect = dom.getBoundingClientRect();
      return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
    },
    setCurrentStatementHighlight: (range) => {
      const editor = editorRef.current;
      if (!editor) return;
      const prev = currentStatementDecorationsRef.current;
      const nextDecorations = range
        ? [
            {
              range: {
                startLineNumber: range.startLineNumber,
                startColumn: range.startColumn,
                endLineNumber: range.endLineNumber,
                endColumn: range.endColumn,
              },
              options: {
                className: 'sql-studio-current-statement',
                // F21 — span the full editor width per line instead of hugging
                // text width, so the tint doesn't end ragged and read like a
                // stale selection.
                isWholeLine: true,
                // Keep highlight visible in the overview ruler so users can
                // see where their cursor statement lives when scrolled.
                stickiness: 1, // NeverGrowsWhenTypingAtEdges — matches Monaco default for range decorations
              },
            },
          ]
        : [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ids = (editor as any).deltaDecorations?.(prev, nextDecorations) ?? [];
      currentStatementDecorationsRef.current = ids;
    },
    getCursorPosition: () => {
      const editor = editorRef.current;
      if (!editor) return null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pos = (editor as any).getPosition?.();
      if (!pos || typeof pos.lineNumber !== 'number' || typeof pos.column !== 'number') return null;
      return { lineNumber: pos.lineNumber, column: pos.column };
    },
    onDidChangeCursorPosition: (callback: (position: { lineNumber: number; column: number }) => void) => {
      const editor = editorRef.current;
      if (!editor) return () => undefined;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const disposable = (editor as any).onDidChangeCursorPosition?.((event: { position?: { lineNumber: number; column: number } }) => {
        if (event?.position) callback(event.position);
      });
      return () => {
        disposable?.dispose?.();
      };
    },
  }), []);

  // Get dark mode and editor settings (wordWrap can be overridden by prop)
  const darkMode = useAppStore((state) => state.isDarkMode);
  const globalWordWrap = useAppStore((state) => state.editorSettings.wordWrap);
  const wordWrap = wordWrapProp ?? globalWordWrap;
  const tabSize = useAppStore((state) => state.editorSettings.tabSize);
  // Item 3e — in-editor type-mismatch diagnostics. Component-level so toggling
  // the setting re-runs the wiring effect immediately (clears markers on OFF).
  const typeMismatchDiagnosticsEnabled = useAppStore(
    (state) => state.intellisenseSettings.typeMismatchDiagnostics,
  );

  // Plan 20 step 2 — in-house MSSQL live syntax diagnostics. Component-level so
  // toggling the setting clears / re-registers the `sql-syntax` pass immediately.
  const liveSyntaxDiagnosticsEnabled = useAppStore(
    (state) => state.intellisenseSettings.liveSyntaxDiagnostics,
  );

  // Safe-mode master gate — when OFF, ALL live diagnostics stop: the
  // `sql-typecheck` and `sql-syntax` effects clear + skip (they take this as an
  // effect-dep so they re-register on toggle), and the LSP `onDiagnostics`
  // listener (owner `'sql'`) reads it via getState() and drops its payload.
  const liveDiagnosticsMasterEnabled = useAppStore(
    (state) => isLiveDiagnosticsEnabled(state.intellisenseSettings),
  );

  // IntelliSense master switch (Settings → Advanced). Component-level so the
  // code-action / inlay-hint registrations and the Monaco option block react
  // immediately; the finer gates below already AND it in via intellisenseGates.
  const intellisenseEnabled = useAppStore(
    (state) => state.intellisenseSettings.enabled,
  );
  // `wordBasedSuggestions` is owned by `syncWordBasedSuggestions` alone, not by
  // the `<Editor options>` block below: the wrapper re-applies that object on
  // every render, so carrying the option there reset the row-limit scope to ON
  // whenever the editor re-rendered with the caret inside the clause. The master
  // switch re-runs the sync instead.
  useEffect(() => {
    if (editorRef.current) syncWordBasedSuggestions(editorRef.current);
  }, [intellisenseEnabled, syncWordBasedSuggestions]);

  // Item C3 — semantic type-on-hover. Component-level so toggling the setting
  // re-registers / disposes the hover provider immediately.
  const semanticTypeHoverEnabled = useAppStore(
    (state) => state.intellisenseSettings.semanticTypeHover,
  );

  // Safe-mode master gate for hover + signature help — when OFF, the five
  // standalone hover / sig-help providers dispose + skip re-registering (they
  // take this as an effect-dep so they toggle immediately), and the LSP
  // hover / sig-help bodies read it via getState() and return null (the LSP
  // effect owns document registration, so it must NOT re-register on toggle).
  const hoverAssistEnabled = useAppStore(
    (state) => isHoverAssistEnabled(state.intellisenseSettings),
  );

  // "Suggest while typing" master switch — when OFF the suggest widget never
  // opens on its own: it flows into `<Editor options>` as `quickSuggestions` /
  // `suggestOnTriggerCharacters` (re-applied by @monaco-editor/react on every
  // options change) and suppresses the cold→warm retrigger below. Ctrl+Space
  // (a manual trigger) keeps working, so the provider itself is not gated.
  const autoSuggestEnabled = useAppStore(
    (state) => isAutoSuggestEnabled(state.intellisenseSettings),
  );

  // The provider is DERIVED from this render's engine, never state set in an
  // effect: every timer and closure a commit starts (document setup,
  // re-register, connect) must talk to the provider of that commit's engine.
  // As state, a tab created with no connection (engine 'mssql') and then bound
  // to a MySQL connection armed its first-setup timer in the same commit that
  // only QUEUED the MySQL provider — so the timer sent SQL Server catalog
  // queries to the MySQL connection and the document stayed on the MSSQL
  // provider for good (field telemetry 2026-09-24).
  const resolvedEngineId = resolveEngineId();
  const languageServiceProvider = useMemo<LanguageServiceProvider>(
    () => getLanguageServiceProvider(resolvedEngineId),
    [resolvedEngineId],
  );

  useEffect(() => {
    engineIdRef.current = resolvedEngineId;
  }, [resolvedEngineId]);

  useEffect(() => {
    languageServiceProvider.ensureInitialized?.().catch(error => {
      console.error('[SqlMonaco] Error initializing language service provider:', error);
    });
  }, [languageServiceProvider]);

  // State to track language server and document status
  const [isLanguageServerReady, setIsLanguageServerReady] = useState<boolean>(false);
  const [isEditorMounted, setIsEditorMounted] = useState<boolean>(false);
  const [documentUri, setDocumentUri] = useState<string>('');
  const [isDocumentRegistered, setIsDocumentRegistered] = useState<boolean>(false);
  // Bumped by the post-mount language-reconciliation effect (below) to force the
  // initial-document-setup effect to re-run for a newly-resolved engine/provider
  // after `editorLanguage` changes. It's a no-op for tabs whose language never
  // changes (SQL-only flows), so those keep their exact prior behavior.
  const [documentSetupNonce, setDocumentSetupNonce] = useState(0);

  // Refs that mirror props/state the completion provider's handler needs to
  // read at call time. The completion provider is registered eagerly (before
  // the LSP handshake completes) so its handler MUST read these dynamically
  // from refs — capturing them by closure would freeze them at the empty /
  // null values present at registration time.
  const documentUriRef = useRef<string>('');
  const connectionIdRef = useRef<string | null | undefined>(connectionId);
  const databaseRef = useRef<string | null | undefined>(database);
  const languageServiceProviderRef = useRef<LanguageServiceProvider>(languageServiceProvider);
  // navigational-features Phase 1 — code-action registry. Capability slices
  // 10–14 register their fix factories here; Slice 4 ships the empty registry
  // + provider wiring. PRD: Ralph/NavigationalFeatures/PRD.md § Slice 4.
  const codeActionRegistryRef = useRef(createCodeActionRegistry());
  // Slices 10–14 register their fix factories once per editor. Stable across
  // re-renders thanks to the ref's `useRef` identity. Add new entries in this
  // block as future capability slices land.
  useEffect(() => {
    const registry = codeActionRegistryRef.current;
    registry.register(addColumnToGroupByEntry);
    registry.register(qualifyAmbiguousColumnEntry);
    registry.register(addMissingJoinPredicateEntry);
    registry.register(addMissingTableToFromEntry);
    registry.register(extractSubqueryToCteEntry);
    registry.register(quoteNumericLiteralEntry);
    registry.register(fixKeywordTypoEntry);
    return () => {
      registry.unregister(addColumnToGroupByEntry.id);
      registry.unregister(qualifyAmbiguousColumnEntry.id);
      registry.unregister(addMissingJoinPredicateEntry.id);
      registry.unregister(addMissingTableToFromEntry.id);
      registry.unregister(extractSubqueryToCteEntry.id);
      registry.unregister(quoteNumericLiteralEntry.id);
      registry.unregister(fixKeywordTypoEntry.id);
    };
  }, []);
  useEffect(() => {
    documentUriRef.current = documentUri;
  }, [documentUri]);
  useEffect(() => {
    connectionIdRef.current = connectionId;
  }, [connectionId]);
  useEffect(() => {
    databaseRef.current = database;
  }, [database]);
  useEffect(() => {
    languageServiceProviderRef.current = languageServiceProvider;
  }, [languageServiceProvider]);

  // Store previous values to detect actual changes
  const previousConnectionId = useRef<string | null>(null);
  const previousDatabase = useRef<string | null>(null);

  const activeConnectionsForContext = useConnectionStore((state) => state.activeConnections);

  const languageServiceDatabase = (() => {
    const explicitDatabase = database?.trim();
    if (explicitDatabase) return explicitDatabase;
    if (!connectionId) return undefined;

    const resolvedEngineId = resolveEngineId();
    if (resolvedEngineId === 'sqlite') return 'main';

    const activeConnection = activeConnectionsForContext.find((conn) => conn.id === connectionId);
    const defaultDatabase = activeConnection?.connection?.database;
    if (typeof defaultDatabase === 'string' && defaultDatabase.trim()) {
      return defaultDatabase.trim();
    }

    return undefined;
  })();

  // Helper function to connect to database. `provider` is the one the document
  // was just registered with — passed explicitly so the connect can never land
  // on a different engine's provider than the registration did.
  const connectToDatabase = async (provider: LanguageServiceProvider, uri: string, connId: string, db: string) => {
    try {
      let serverName = 'localhost';
      let username = '';

      const { activeConnections } = useConnectionStore.getState();
      const activeConnection = activeConnections.find(conn => conn.id === connId);

      if (activeConnection?.connection) {
        serverName = activeConnection.connection.server;
        if (activeConnection.connection.authentication === 'SQL Server authentication') {
          username = activeConnection.connection.login || '';
        }
      }

      if (!serverName || serverName.trim() === '' || serverName === connId) {
        serverName = 'localhost';
      }

      const connectionInfo: LanguageServiceConnectionInfo = {
        connectionId: connId,
        database: db,
        host: serverName,
        user: username,
      };

      // Register the document's lifecycle with the sync manager BEFORE the
      // schema load, not only on its success: the manager owns teardown on
      // close and re-connection on reconnect, and a document whose first
      // load failed (server busy, schema query throttled) must still get
      // both — otherwise a later successful Refresh IntelliSense recovers
      // its schema but leaves it invisible to disconnect / reconnect. With its
      // provider and database, so a reconnect goes through the provider that
      // holds the document and reloads THIS database, never the connection's
      // engine or default database.
      registerDocumentForConnection(connId, uri, { provider, database: db });
      await provider.connectForLanguageServices(uri, connectionInfo);
    } catch (error) {
      console.error('[SqlMonaco] Error connecting to database:', error);
    }
  };

  // The deferred re-register below is cancelled on the next re-register and
  // on unmount: a tab closed inside its 300 ms window must not register a
  // URI the unmount cleanup already handed to `unregisterDocumentForConnection`.
  const registerAndConnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-register the document whenever the tab's connection/database changes,
  // including the first time it cascades in from the parent (tabs can mount
  // with undefined connectionId while `hasBeenActive` is still false, so the
  // initial setup effect registers an `untitled/...` URI — once the real
  // connection arrives we need to tear that down and register a fresh URI
  // that carries connectionId/database in its path, otherwise SSTS keeps
  // returning schema-unbound keyword-only completions).
  useEffect(() => {
    if (!isDocumentRegistered || !connectionId || !languageServiceDatabase) {
      return;
    }

    const connectionChanged = connectionId !== previousConnectionId.current;
    const databaseChanged = languageServiceDatabase !== previousDatabase.current;

    if (!connectionChanged && !databaseChanged) return;
    if (!documentUri) return;

    const newUri = createDocumentUri({ connectionId, database: languageServiceDatabase });
    const oldUriToClose = documentUri;

    const oldConnectionId = getConnectionIdFromDocumentUri(oldUriToClose);
    if (oldConnectionId) {
      unregisterDocumentForConnection(oldConnectionId, oldUriToClose);
    }
    // Close through the provider that holds the old document — when the
    // engine switched together with the connection, that is not this
    // render's provider.
    (documentProviderRef.current ?? languageServiceProvider).closeDocument(oldUriToClose).catch(() => {});
    setIsDocumentRegistered(false);

    setDocumentUri(newUri);
    registeredUriRef.current = newUri;
    const provider = languageServiceProvider;
    documentProviderRef.current = provider;

    const registerAndConnect = async () => {
      try {
        const registered = await provider.registerDocument(
          newUri,
          readModelTextSafe(editorRef.current?.getModel()) || '',
        );
        if (registered) {
          setIsDocumentRegistered(true);
          await connectToDatabase(provider, newUri, connectionId, languageServiceDatabase);
        } else {
          setIsDocumentRegistered(false);
        }
      } catch {
        setIsDocumentRegistered(false);
      }
    };

    if (registerAndConnectTimerRef.current !== null) clearTimeout(registerAndConnectTimerRef.current);
    registerAndConnectTimerRef.current = setTimeout(() => {
      registerAndConnectTimerRef.current = null;
      void registerAndConnect();
    }, 300);

    previousConnectionId.current = connectionId;
    previousDatabase.current = languageServiceDatabase;
  }, [isDocumentRegistered, connectionId, languageServiceDatabase, documentUri, languageServiceProvider]);

  // Initialize ConnectionSyncManager on first render
  useEffect(() => {
    initializeConnectionSyncManager();
  }, []);

  // Setup language server connection.
  //
  // Historically (pre-§10 steps 6/7), `isLanguageServerReady` gated the
  // initial document-setup effect below on SSTS's (MSSQL's SqlToolsService)
  // observed status: MSSQL's `registerDocument` used to send
  // `textDocument/didOpen` straight to SSTS, and calling that before SSTS had
  // finished starting threw "SQL Tools Service is not running" and silently
  // failed registration. That rationale no longer applies to ANY engine:
  // step 6 moved mssql's `registerDocument`/`connectForLanguageServices` to
  // an in-house, renderer-local provider (`mssqlLanguageServiceProvider`,
  // mirroring sqlite/pg/mysql/oracle) that never touches SSTS, and step 7
  // deleted the SSTS startup bootstrap entirely — SSTS now only launches
  // lazily, on demand, for the retained Windows Named-Pipes/Shared-Memory
  // transport and DacFx BACPAC, neither of which is on this document-setup
  // path. There is therefore nothing left to wait for: every engine's
  // provider manages its own document registration synchronously/locally, so
  // mark the language server ready immediately, same as the other engines
  // always did.
  useEffect(() => {
    setIsLanguageServerReady(true);
    languageServiceProvider.ensureInitialized?.().catch(() => {});
  }, [languageServiceProvider]);

  // Track if initial document setup has been done
  const initialDocumentSetupDone = useRef(false);
  // Track the currently-registered URI so the unmount effect (below) can close
  // whatever document is registered at teardown time — including a URI created
  // later by the post-mount reconnect effect when the tab's connection changes.
  const registeredUriRef = useRef<string | null>(null);
  // The provider that URI is registered with (set when the registration is
  // scheduled). Teardown and re-wiring go through THIS provider, not the
  // current render's: after an engine switch the two differ, and the old
  // engine's provider is the one holding the document.
  const documentProviderRef = useRef<LanguageServiceProvider | null>(null);
  // Handle for the initial-document-setup timer so the language-reconciliation
  // effect (below) can cancel a registration that was scheduled with the
  // now-stale (pre-switch) provider before it fires.
  const documentSetupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Setup document with language server when Monaco and editor are ready (initial setup only).
  //
  // NOTE: no cleanup returned from this effect on purpose. Its deps still
  // change after mount (`isLanguageServerReady` flips exactly once now — set
  // unconditionally by the effect above, §10 step 7 — and `isEditorMounted` /
  // the provider can settle on later renders). A cleanup here would fire on
  // those flips — calling closeDocument mid-setup and deleting the
  // freshly-registered document status, which makes the PG 2s ready-transition
  // see `undefined` and lose the document. Document teardown is instead
  // handled by a dedicated unmount-only effect below.
  useEffect(() => {
    if (!usesLanguageServiceDocument) return; // kusto included; skip pure non-service languages
    if (!isEditorMounted || !isLanguageServerReady) return;
    if (!monacoRef.current || !editorRef.current) return;
    if (initialDocumentSetupDone.current) return;

    // Editors bound to a DB engine — query-editor tabs etc. — receive their
    // connectionId/database on a later render than their initial mount (the
    // parent tab gates them behind `hasBeenActive`). Wait until those cascade
    // before registering, otherwise we'd register with an `untitled/...` URI
    // that SSTS cannot bind a schema to, producing keyword-only completions
    // until a follow-up re-register dance runs — which races against user
    // typing. engineId alone signals "this editor targets a DB engine",
    // so it's the right gate: absent engineId we register immediately so
    // plain untitled SQL editors still get the completion pipeline.
    if (engineId && (!connectionId || !languageServiceDatabase)) return;

    initialDocumentSetupDone.current = true;

    const uri = createDocumentUri({ connectionId, database: languageServiceDatabase });

    setDocumentUri(uri);
    registeredUriRef.current = uri;
    // This commit's provider — derived from this commit's engine, so a tab
    // whose engine resolved in the same commit as its connection registers
    // (and connects) with the new engine's provider, not the previous one.
    const provider = languageServiceProvider;
    documentProviderRef.current = provider;

    const initializeDocument = async () => {
      try {
        // Read the LIVE model, not the `value` prop this effect closed over.
        // Registration is deferred behind a 200 ms timer, and any edit landing
        // in that window is invisible to the LSP: `handleEditorChange` skips
        // its update while `isDocumentRegistered` is false, so a stale `value`
        // here becomes the buffer the server holds — with no further edit
        // pending to correct it. The re-register path below already reads the
        // model; this one has to agree.
        const registered = await provider.registerDocument(
          uri,
          readModelTextSafe(editorRef.current?.getModel()) ?? value ?? '',
        );
        if (registered) {
          setIsDocumentRegistered(true);
          if (connectionId && languageServiceDatabase) {
            previousConnectionId.current = connectionId;
            previousDatabase.current = languageServiceDatabase;
            connectToDatabase(provider, uri, connectionId, languageServiceDatabase);
          }
        }
      } catch {
        // Silent fail
      }
    };

    documentSetupTimerRef.current = setTimeout(initializeDocument, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditorMounted, isLanguageServerReady, engineId, connectionId, languageServiceDatabase, languageServiceProvider, documentSetupNonce]);

  // Unmount-only cleanup for the registered document. Kept separate from the
  // setup effect so SSTS status flaps don't trigger document teardown mid-setup.
  useEffect(() => {
    return () => {
      // A setup / re-register still waiting in its timer must not fire after
      // the teardown below — it would register a URI already unregistered
      // and the sync manager would re-sync a dead document on every reconnect.
      if (documentSetupTimerRef.current !== null) {
        clearTimeout(documentSetupTimerRef.current);
        documentSetupTimerRef.current = null;
      }
      if (registerAndConnectTimerRef.current !== null) {
        clearTimeout(registerAndConnectTimerRef.current);
        registerAndConnectTimerRef.current = null;
      }
      const uri = registeredUriRef.current;
      if (!uri) return;
      const connId = getConnectionIdFromDocumentUri(uri);
      if (connId) {
        unregisterDocumentForConnection(connId, uri);
      }
      // The mount render's provider is stale once the engine has changed;
      // close through the one holding the document.
      (documentProviderRef.current ?? languageServiceProviderRef.current).closeDocument(uri).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce-trailing LSP `updateDocument` driven by Monaco's content-change
  // event (see handleEditorChange below). The previous design *throttled*
  // (one IPC every 300ms during typing) — but each LSP response handler
  // runs sync in the renderer and can absorb a frame's worth of work mid
  // burst, which shows up as stutter on a high-refresh display. Trailing
  // debounce means: zero LSP IPC during a typing burst, exactly one
  // updateDocument 250ms after typing stops, with the final buffer.
  const lspUpdateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (lspUpdateTimerRef.current !== null) clearTimeout(lspUpdateTimerRef.current);
    };
  }, []);

  // Track LSP-gated provider disposables (hover / signature help / diagnostics)
  // to prevent duplicate registrations. The completion provider lives in its
  // own ref because it is registered eagerly (no LSP gate) — see the dedicated
  // effect further below.
  const providersRef = useRef<{
    hover: monacoNs.IDisposable;
    signatureHelp: monacoNs.IDisposable;
    diagnostics: (() => void) | null;
  } | null>(null);
  const completionDisposableRef = useRef<{ dispose: () => void } | null>(null);
  // Inlay hints disposable is owned by its own effect (see below) — it is
  // intentionally decoupled from the LSP handshake so inlay hints work even
  // when the SQL Tools Service isn't running (e.g. E2E test mode).
  const inlayHintsDisposableRef = useRef<monacoNs.IDisposable | null>(null);
  // Item 3e — debounce timer for the type-mismatch diagnostics pass, plus a
  // once-per-mount guard so the analytics event fires only on first emit.
  const typeCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typeCheckAnalyticsFiredRef = useRef<boolean>(false);
  const syntaxDiagAnalyticsFiredRef = useRef<boolean>(false);
  // Item C3 — once-per-mount guard for the semantic-hover analytics event.
  const semanticHoverAnalyticsFiredRef = useRef<boolean>(false);

  // Completion telemetry handle. Wired once the editor mounts; disposed on
  // unmount. The completion provider below records each orchestrator run so
  // accept events can compute latency and tag positionKind.
  const completionTelemetryRef = useRef<CompletionTelemetryHandle | null>(null);
  useEffect(() => {
    if (!isEditorMounted || !editorRef.current) return;
    const handle = wireCompletionTelemetry(editorRef.current, {
      track: (eventName, data) => {
        if (typeof window === 'undefined') return;
        const api = window.electronAPI?.analytics?.track;
        if (typeof api !== 'function') return;
        api(eventName, data).catch(() => {
          // Telemetry is fire-and-forget; never surface IPC errors to the editor.
        });
      },
      getAnalyticsEnabled: () => useAppStore.getState().analyticsEnabled,
      getEngineId: () => engineIdRef.current,
      // Ranking level 2 — accepted completions float up next time, scoped to
      // the (connection, database) this editor completes against.
      onAccepted: (label) =>
        recordAcceptedCompletion(
          { connectionId: connectionIdRef.current ?? undefined, database: databaseRef.current ?? undefined },
          label,
        ),
    });
    completionTelemetryRef.current = handle;
    return () => {
      handle.dispose();
      completionTelemetryRef.current = null;
    };
  }, [isEditorMounted]);

  // Register the completion provider INDEPENDENTLY of the LSP handshake.
  //
  // Completion is now orchestrator-driven; the dialect-native resolvers
  // (`src/features/intellisense/resolvers/`) read from the schema bus and
  // never touch the LSP. Bundling completion registration with the LSP-gated
  // providers below made completion silently unavailable any time the LSP
  // hadn't bound the document yet — most notably for MSSQL when SSTS is
  // slow to start, or for any engine in environments where SSTS is skipped
  // (E2E test mode). The handler reads `documentUri`, `connectionId`,
  // `database`, and `languageServiceProvider` from refs at call time so an
  // early registration still picks up the values once they cascade.
  useEffect(() => {
    if (!isSqlLanguage) return;
    if (!monacoRef.current || !isEditorMounted) return;

    if (completionDisposableRef.current) {
      completionDisposableRef.current.dispose();
      completionDisposableRef.current = null;
    }

    const monaco = monacoRef.current;

    ensureSnippetInsertCommand(monaco);

    // Register completion provider
    const completionDisposable = monaco.languages.registerCompletionItemProvider(editorLanguage, {
      triggerCharacters: ['.', ' ', ':', ','],
      provideCompletionItems: async (
        model: monacoEditor.ITextModel,
        position: monacoNs.Position,
        context: monacoNs.languages.CompletionContext,
        token?: monacoNs.CancellationToken
      ) => {
        // E2E-only capture hook. Gated on `window.__jamSqlE2eCompletionCapture__`
        // being a function — production is zero-cost. Fired on EVERY exit path
        // (model-mismatch guard, orchestrator error, success) with an
        // `exitPath` discriminator, so the diagnostic spec can pin not just
        // which pipeline stage drops items but why the provider returned what
        // it did when it contributes nothing at all.
        const e2eCapture = (window as unknown as {
          __jamSqlE2eCompletionCapture__?: (info: Record<string, unknown>) => void;
        }).__jamSqlE2eCompletionCapture__;
        const captureExit = (info: Record<string, unknown>) => {
          if (typeof e2eCapture !== 'function') return;
          try {
            e2eCapture(info);
          } catch {
            // never break completion if the capture hook throws
          }
        };
        captureExit({
          exitPath: 'enter',
          orchestratorSuggestions: [],
          snippetSuggestions: [],
          merged: [],
          typedWord: '',
          triggerCharacter: context?.triggerCharacter ?? '',
          engineId: engineIdRef.current,
        });

        if (editorRef.current?.getModel?.() !== model) {
          captureExit({
            exitPath: 'model-mismatch',
            orchestratorSuggestions: [],
            snippetSuggestions: [],
            merged: [],
            typedWord: '',
            triggerCharacter: '',
            engineId: engineIdRef.current,
          });
          return { suggestions: [] };
        }

        // Safe-mode gate — when semantic completion is OFF, contribute nothing
        // so Monaco's built-in word-based suggestions take over. Read live via
        // getState() (this effect owns provider registration, so it must NOT
        // re-register on toggle — mirrors the hover/sig-help getState() gates).
        // Snippets are intentionally suppressed too: safe mode stays maximally
        // inert, so this returns before any snippet/orchestrator work happens.
        // IntelliSense master switch OFF — contribute nothing at all (no
        // snippets, no orchestrator, no word-based fallback either: the option
        // block turns `wordBasedSuggestions` off alongside). Read live via
        // getState() for the same re-registration reason as the gates below.
        if (!isCompletionEnabled(useAppStore.getState().intellisenseSettings)) {
          captureExit({
            exitPath: 'intellisense-disabled',
            orchestratorSuggestions: [],
            snippetSuggestions: [],
            merged: [],
            typedWord: '',
            triggerCharacter: context?.triggerCharacter ?? '',
            engineId: engineIdRef.current,
          });
          return { suggestions: [] };
        }

        if (!isSemanticCompletionEnabled(useAppStore.getState().intellisenseSettings)) {
          captureExit({
            exitPath: 'semantic-disabled',
            orchestratorSuggestions: [],
            snippetSuggestions: [],
            merged: [],
            typedWord: '',
            triggerCharacter: context?.triggerCharacter ?? '',
            engineId: engineIdRef.current,
          });
          return { suggestions: [] };
        }

        // 1.5.1 F2 — Monaco calls this provider synchronously from inside the
        // keystroke (trigger characters, `incomplete` re-queries), so all the
        // work below used to hold back the paint of the character just typed.
        // Yield first (every engine — one provider), then drop a request the
        // user out-typed meanwhile before it costs anything: Monaco discards
        // a cancelled request's result anyway. Not a telemetry "run" — the
        // lat_* clock (`recordOrchestratorRun`) has not started. No capture
        // exit either: e2e harnesses read the newest COMPLETED capture as the
        // list on screen, and a dropped request puts nothing on screen.
        await yieldToEventLoop();
        if (token?.isCancellationRequested) return { suggestions: [] };
        const providerCost = startProviderCost();

        const textUntilPosition = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column
        });

        const triggerChar = position.column > 1
          ? textUntilPosition.charAt(textUntilPosition.length - 1)
          : '';

        const wordInfo = model.getWordUntilPosition(position);
        const fullText = model.getValue();
        const cursorOffset = model.getOffsetAt(position);
        const statementText = getStatementTextBeforeCursor(fullText, cursorOffset);

        const snippetEngineId = normalizeSnippetEngineId(engineIdRef.current);
        const customSnippets = useSqlSnippetsStore.getState().getSnippetsForEngine(snippetEngineId);

        const snippetCompletions = getSqlSnippetCompletions({
          engineId: engineIdRef.current,
          statementTextBeforeCursor: statementText,
          currentWord: wordInfo.word || '',
          triggerKind: context?.triggerKind,
          triggerCharacter: context?.triggerCharacter ?? triggerChar,
          customSnippets,
          // Inside a routine/trigger body the post-`;` slot is not a real
          // top-level statement start; suppress the full builtin-template flood
          // so declared locals + keywords stay above Monaco's virtualized fold.
          insideProceduralBody: isInsideRoutineBodyBlock(fullText, cursorOffset, engineIdRef.current),
        });

        const snippetSuggestions = snippetCompletions.map((snippet) => ({
          label: snippet.label,
          kind: monaco.languages.CompletionItemKind.Snippet,
          detail: snippet.detail,
          documentation: snippet.documentation,
          insertText: snippet.insertText,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          sortText: snippet.sortText,
          filterText: snippet.filterText,
          range: {
            startLineNumber: position.lineNumber,
            startColumn: wordInfo.startColumn,
            endLineNumber: position.lineNumber,
            endColumn: wordInfo.endColumn,
          },
          // Fires after Monaco inserts the snippet (acceptance signal).
          command: {
            id: SNIPPET_INSERT_COMMAND_ID,
            title: 'Track snippet insert',
            arguments: [
              engineIdRef.current,
              snippet.id.startsWith('custom:') ? 'custom' : snippet.id,
            ],
          },
        }));

        const registry = getResolverRegistry();
        const intellisenseSettings = useAppStore.getState().intellisenseSettings;
        const currentConnectionId = connectionIdRef.current ?? undefined;
        const currentDatabase = databaseRef.current ?? undefined;
        const versionGatingHint = computeVersionGatingHint(
          engineIdRef.current,
          currentConnectionId,
          currentDatabase,
          fullText,
          intellisenseSettings.autocompleteVersionFilter,
        );
        // Scope-changing statements before the cursor (USE / SET search_path /
        // ALTER SESSION SET CURRENT_SCHEMA / ATTACH DATABASE) always feed
        // completion — a whole-buffer Execute runs them, so completion below
        // them must resolve against the scope they establish.
        const scriptScope = computeScriptScopeFromBuffer(fullText, cursorOffset, engineIdRef.current);
        completionTelemetryRef.current?.recordOrchestratorRun({});
        let orchestratorResult;
        try {
          orchestratorResult = await runOrchestrator(
            model,
            position,
            { triggerKind: context?.triggerKind, triggerCharacter: context?.triggerCharacter ?? triggerChar },
            {
              engineId: engineIdRef.current,
              documentUri: documentUriRef.current,
              connectionId: currentConnectionId,
              database: currentDatabase,
              languageServiceProvider: languageServiceProviderRef.current,
              monaco,
              statementTextBeforeCursor: statementText,
              resolverFor: (id) => registry.get(id),
              keywordCasing: {
                mode: intellisenseSettings.keywordCasing,
                typedFragment: wordInfo.word || '',
                bufferText: fullText,
              },
              versionGating: versionGatingHint,
              sqliteRevealShadowTables: intellisenseSettings.sqliteRevealShadowTables,
              scriptScope,
            },
          );
        } catch (err) {
          completionTelemetryRef.current?.recordOrchestratorError('resolve', err);
          captureExit({
            exitPath: 'orchestrator-error',
            error: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
            errorStack: err instanceof Error ? (err.stack ?? '').slice(0, 2000) : '',
            orchestratorSuggestions: [],
            snippetSuggestions: snippetSuggestions.slice(),
            merged: [],
            typedWord: wordInfo.word || '',
            triggerCharacter: context?.triggerCharacter ?? triggerChar,
            engineId: engineIdRef.current,
          });
          return { suggestions: snippetSuggestions };
        }

        const suggestions = mergeCompletionSuggestions(snippetSuggestions, orchestratorResult.suggestions);
        // Main-thread cost of this request (`prov_*` buckets) — undefined
        // when a resolver awaited real I/O and elapsed time would count the wait.
        const providerMs = providerCost.finish();
        if (token?.isCancellationRequested) {
          // Monaco will discard this result — the user out-typed the run.
          // Telemetry-only branch: the return value below stays identical, so
          // behavior is unchanged; the run just counts as superseded instead
          // of polluting the latency histogram with an undeliverable sample.
          completionTelemetryRef.current?.recordOrchestratorSuperseded({ providerMs });
        } else {
          completionTelemetryRef.current?.recordOrchestratorResolved({
            suggestionCount: suggestions.length,
            positionKind: orchestratorResult.positionKind,
            classifiedBy: orchestratorResult.classifiedBy,
            valueSlotRole: orchestratorResult.valueSlotRole,
            predicateHeadRole: orchestratorResult.predicateHeadRole,
            providerMs,
            truncated: orchestratorResult.truncated,
          });
        }
        captureExit({
          exitPath: 'success',
          orchestratorSuggestions: orchestratorResult.suggestions.slice(),
          snippetSuggestions: snippetSuggestions.slice(),
          merged: suggestions.slice(),
          typedWord: wordInfo.word || '',
          triggerCharacter: context?.triggerCharacter ?? triggerChar,
          engineId: engineIdRef.current,
        });
        return { suggestions, incomplete: orchestratorResult.incomplete };
      }
    });

    completionDisposableRef.current = completionDisposable;

    return () => {
      if (completionDisposableRef.current === completionDisposable) {
        completionDisposableRef.current = null;
      }
      completionDisposable.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditorMounted, isSqlLanguage, editorLanguage]);

  // Register hover / signature help / diagnostics providers when the document
  // is registered with the LSP. These genuinely need the LSP — hover queries
  // the LSP for object info, signature help asks the LSP for parameter
  // metadata, and diagnostics stream from LSP markers.
  useEffect(() => {
    if (!isSqlLanguage) return;
    if (!monacoRef.current || !documentUri || !isDocumentRegistered) return;

    if (providersRef.current) {
      providersRef.current.hover?.dispose();
      providersRef.current.signatureHelp?.dispose();
      providersRef.current.diagnostics?.();
      const monaco = monacoRef.current;
      const model = editorRef.current?.getModel();
      if (monaco && model) {
        monaco.editor.setModelMarkers(model, 'sql', []);
      }
      providersRef.current = null;
    }

    const monaco = monacoRef.current;

    // Register hover provider (LSP-backed hover info — object details, etc.).
    // The SELECT * expansion hover is registered separately below so it works
    // even when the SQL Tools Service is not running (e.g. E2E test mode).
    const hoverDisposable = monaco.languages.registerHoverProvider(editorLanguage, {
      provideHover: async (model: monacoEditor.ITextModel, position: monacoNs.Position) => {
        // Safe-mode gate — read live via getState() (this effect owns document
        // registration, so it must NOT re-register on toggle).
        if (!isHoverAssistEnabled(useAppStore.getState().intellisenseSettings)) return null;
        try {
          if (editorRef.current?.getModel?.() !== model) {
            return { contents: [] };
          }

          const hoverResult = await languageServiceProvider.getHoverInfo(
            documentUri,
            { line: position.lineNumber - 1, character: position.column - 1 }
          );

          if (!hoverResult?.contents) {
            return { contents: [] };
          }

          const contents = buildHoverContents(hoverResult.contents);
          if (!contents.length) {
            return { contents: [] };
          }

          return {
            contents,
            range: hoverResult.range ? {
              startLineNumber: hoverResult.range.start.line + 1,
              startColumn: hoverResult.range.start.character + 1,
              endLineNumber: hoverResult.range.end.line + 1,
              endColumn: hoverResult.range.end.character + 1
            } : undefined
          };
        } catch {
          return { contents: [] };
        }
      }
    });

    // Register signature help provider
    const signatureHelpDisposable = monaco.languages.registerSignatureHelpProvider(editorLanguage, {
      signatureHelpTriggerCharacters: ['(', ','],
      provideSignatureHelp: async (
        model: monacoEditor.ITextModel,
        position: monacoNs.Position,
        token?: monacoNs.CancellationToken
      ) => {
        // Safe-mode gate — read live via getState() (this effect owns document
        // registration, so it must NOT re-register on toggle).
        if (!isHoverAssistEnabled(useAppStore.getState().intellisenseSettings)) return null;
        try {
          if (editorRef.current?.getModel?.() !== model) {
            return null;
          }

          const signatureResult = await languageServiceProvider.getSignatureHelp(
            documentUri,
            { line: position.lineNumber - 1, character: position.column - 1 }
          );

          if (editorRef.current?.getModel?.() !== model) return null;
          if (token?.isCancellationRequested) return null;
          if (!signatureResult?.signatures?.length) {
            return null;
          }

          const signatureHelpValue = {
            signatures: signatureResult.signatures.map((sig: LspSignatureInformation) => ({
              label: sig.label,
              documentation:
                (typeof sig.documentation === 'object' ? sig.documentation?.value : sig.documentation) ||
                '',
              parameters: sig.parameters?.map((param: LspParameterInformation) => ({
                label: param.label,
                documentation:
                  (typeof param.documentation === 'object'
                    ? param.documentation?.value
                    : param.documentation) || ''
              })) || []
            })),
            activeSignature: signatureResult.activeSignature || 0,
            activeParameter: signatureResult.activeParameter || 0
          };
          // Defensive shape check: Monaco 0.52.2's ParameterHintsModel.doTrigger
          // reads `.value.signatures` without guarding the `.value` property, so
          // any return lacking a non-empty `signatures` array crashes the editor.
          if (!signatureHelpValue.signatures.length) return null;
          return { value: signatureHelpValue, dispose: () => {} };
        } catch {
          return null;
        }
      }
    });

    const removeDiagnosticsListener = languageServiceProvider.onDiagnostics?.((payload) => {
      // Safe-mode master gate — read live via getState() (this effect's deps are
      // connection-scoped, so it must NOT re-register on toggle). When OFF, drop
      // the LSP payload and clear any lingering `sql`-owner markers.
      if (!isLiveDiagnosticsEnabled(useAppStore.getState().intellisenseSettings)) {
        const model = editorRef.current?.getModel();
        if (monaco && model) monaco.editor.setModelMarkers(model, 'sql', []);
        return;
      }
      if (payload.uri !== documentUri) return;

      const model = editorRef.current?.getModel();
      if (!monaco || !model) return;

      // Plan 20 step 2 (Decision 3): MSSQL syntax diagnostics are produced
      // in-house under the `sql-syntax` owner (below). SqlToolsService's
      // `publishDiagnostics` is retired for mssql — dropping its payload here
      // guarantees exactly ONE syntax-squiggle source at a time. SSTS is NOT a
      // fallback: even when the in-house pass is disabled, these stay dropped.
      // pgls (pgsql) keeps flowing through the suppressor path untouched.
      if (engineIdRef.current === 'mssql') return;

      const severityMap: Record<number, number> = {
        1: monaco.MarkerSeverity.Error,
        2: monaco.MarkerSeverity.Warning,
        3: monaco.MarkerSeverity.Info,
        4: monaco.MarkerSeverity.Hint,
      };

      const markers = (payload.diagnostics || []).map((diag: LanguageServiceDiagnostic) => ({
        startLineNumber: (diag.range?.start?.line ?? 0) + 1,
        startColumn: (diag.range?.start?.character ?? 0) + 1,
        endLineNumber: (diag.range?.end?.line ?? 0) + 1,
        endColumn: (diag.range?.end?.character ?? 0) + 1,
        message: diag.message || 'SQL error',
        severity: severityMap[diag.severity ?? 1] ?? monaco.MarkerSeverity.Error,
        source: diag.source,
        code: toMarkerCode(diag.code),
      }));

      // Diagnostics suppression — drop SSTS / pgls false positives
      // (`OPENJSON` inside an MSSQL CTE, `RETURNING` inside a PG CTE-DML)
      // and tree-sitter low-confidence ranges. Tree parse is async; the
      // marker-set is fan-out, so a brief async hop is acceptable.
      void applyDiagnosticsSuppressionAndSet(monaco, model, markers, engineIdRef.current, connectionIdRef.current ?? undefined, databaseRef.current ?? undefined);
    });

    providersRef.current = {
      hover: hoverDisposable,
      signatureHelp: signatureHelpDisposable,
      diagnostics: removeDiagnosticsListener || null,
    };

    return () => {
      if (providersRef.current) {
        providersRef.current.hover?.dispose();
        providersRef.current.signatureHelp?.dispose();
        providersRef.current.diagnostics?.();
        providersRef.current = null;
      }

      const model = editorRef.current?.getModel();
      if (monaco && model) {
        monaco.editor.setModelMarkers(model, 'sql', []);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentUri, isDocumentRegistered, languageServiceProvider]);

  // Register inlay hints provider INDEPENDENTLY of the LSP handshake.
  //
  // Inlay hints are a pure text-parsing feature (for explicit-column-list INSERTs)
  // plus an optional per-engine schema lookup (for no-column-list INSERTs). Neither
  // path depends on the SQL Tools Service LSP being up. Bundling inlay hint
  // registration with the other providers (completion/hover/signatureHelp) made
  // inlay hints silently unavailable in any scenario where the LSP isn't ready —
  // most notably E2E test mode, where `main.ts` deliberately skips the SQL Tools
  // Service startup. Decoupling this registration makes inlay hints work the
  // moment Monaco + the editor instance are ready, regardless of LSP state.
  useEffect(() => {
    if (!isSqlLanguage || !hoverAssistEnabled) return;
    if (!monacoRef.current || !isEditorMounted) return;
    const monaco = monacoRef.current;

    const inlayHintsDisposable = monaco.languages.registerInlayHintsProvider(editorLanguage, {
      provideInlayHints: async (
        model: monacoEditor.ITextModel,
        _range: monacoNs.Range,
        token?: monacoNs.CancellationToken
      ) => {
        // Guard 1: model identity check before the async work. Prevents a stale
        // provider call (from a previously-mounted editor instance) from
        // returning hints for a model it no longer owns.
        if (editorRef.current?.getModel?.() !== model) {
          return { hints: [], dispose: () => {} };
        }
        const text = model.getValue();
        const resolvedEngineId = engineIdRef.current;
        const hints = await computeInsertInlayHints(
          text,
          connectionId,
          database,
          resolvedEngineId,
          (offset: number) => {
            const pos = model.getPositionAt(offset);
            return { lineNumber: pos.lineNumber, column: pos.column };
          },
        );

        // Guard 2: re-check model identity and cancellation after the async
        // resolve. If the user switched tabs during the MSSQL IPC fetch, the
        // editor may now own a different model.
        if (editorRef.current?.getModel?.() !== model) {
          return { hints: [], dispose: () => {} };
        }
        if (token?.isCancellationRequested) {
          return { hints: [], dispose: () => {} };
        }

        return {
          hints: hints.map(h => ({
            position: new monaco.Position(h.position.lineNumber, h.position.column),
            label: h.label,
            kind: monaco.languages.InlayHintKind.Parameter,
            paddingRight: h.paddingRight,
          })),
          dispose: () => {},
        };
      },
    });

    inlayHintsDisposableRef.current = inlayHintsDisposable;

    return () => {
      inlayHintsDisposable.dispose();
      if (inlayHintsDisposableRef.current === inlayHintsDisposable) {
        inlayHintsDisposableRef.current = null;
      }
    };
     
  }, [isEditorMounted, isSqlLanguage, editorLanguage, connectionId, database, hoverAssistEnabled]);

  // Item 3e — in-editor type-mismatch diagnostics. Runs the resolver's
  // coercion-aware pass over the buffer on a debounce and on schema-bus
  // updates, publishing markers under the dedicated `sql-typecheck` owner so it
  // composes with (never clobbers) the LSP's own `sql` markers. Registered
  // INDEPENDENTLY of the LSP handshake — it is a pure text+snapshot pass and
  // needs only a loaded schema snapshot, not a running SQL Tools Service / LSP.
  useEffect(() => {
    if (!isSqlLanguage || !isEditorMounted) return;
    if (!typeMismatchDiagnosticsEnabled || !liveDiagnosticsMasterEnabled) {
      const model = editorRef.current?.getModel();
      const monaco = monacoRef.current;
      if (monaco && model) monaco.editor.setModelMarkers(model, 'sql-typecheck', []);
      return;
    }
    const monaco = monacoRef.current;
    if (!monaco) return;

    const TYPE_CHECK_OWNER = 'sql-typecheck';
    const DEBOUNCE_MS = 400;
    const MAX_SOURCE_LEN = 200_000; // skip pathologically large buffers

    const clearMarkers = (): void => {
      const model = editorRef.current?.getModel?.();
      if (model) monaco.editor.setModelMarkers(model, TYPE_CHECK_OWNER, []);
    };

    const recompute = (): void => {
      const model = editorRef.current?.getModel?.();
      if (!model) return;
      const sqlEngine = databaseEngineIdToSqlEngine(engineIdRef.current);
      if (!sqlEngine) { clearMarkers(); return; }
      const snapshot = getSchemaBusSnapshot(
        connectionIdRef.current ?? '',
        databaseRef.current ?? undefined,
      );
      if (!snapshot || snapshot.tables.length === 0) { clearMarkers(); return; }
      const source = model.getValue();
      if (source.length > MAX_SOURCE_LEN) { clearMarkers(); return; }

      const markers = collectTypeCheckMarkers({
        source,
        engine: sqlEngine,
        snapshotTables: snapshot.tables,
      });

      monaco.editor.setModelMarkers(
        model,
        TYPE_CHECK_OWNER,
        markers.map((m) => {
          const start = model.getPositionAt(m.startOffset);
          const end = model.getPositionAt(m.endOffset);
          return {
            startLineNumber: start.lineNumber,
            startColumn: start.column,
            endLineNumber: end.lineNumber,
            endColumn: end.column,
            message: m.message,
            severity: monaco.MarkerSeverity.Warning,
            source: 'type-check',
            code: m.code,
          };
        }),
      );

      if (markers.length > 0 && !typeCheckAnalyticsFiredRef.current) {
        typeCheckAnalyticsFiredRef.current = true;
        window.electronAPI?.analytics
          ?.track('feature:type_check_diagnostics', { engine: sqlEngine })
          .catch(() => {});
      }
    };

    const scheduleRecompute = (): void => {
      if (typeCheckTimerRef.current) clearTimeout(typeCheckTimerRef.current);
      typeCheckTimerRef.current = setTimeout(recompute, DEBOUNCE_MS);
    };

    // Recompute on buffer edits (debounced) and on schema-snapshot changes (the
    // snapshot can land after the document mounts — first warm pull).
    const contentDisposable = editorRef.current?.getModel?.()?.onDidChangeContent(scheduleRecompute);
    const unsubscribeBus = connectionIdRef.current
      ? subscribeSchemaBus(connectionIdRef.current, scheduleRecompute)
      : undefined;

    recompute(); // initial pass

    return () => {
      if (typeCheckTimerRef.current) {
        clearTimeout(typeCheckTimerRef.current);
        typeCheckTimerRef.current = null;
      }
      contentDisposable?.dispose();
      unsubscribeBus?.();
      clearMarkers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditorMounted, isSqlLanguage, editorLanguage, connectionId, database, typeMismatchDiagnosticsEnabled, liveDiagnosticsMasterEnabled]);

  // Plan 20 step 2 + PM-C — in-house live syntax diagnostics, publishing
  // genuine syntax errors under the dedicated `sql-syntax` owner.
  //   • mssql: ANTLR ∧ tree-sitter (the ANTLR parse is WORKER-CARRIED — plan16
  //     wedge fix; only the ms-cheap suppression layers run here). Initial pass
  //     runs immediately: the parse itself is off-thread.
  //   • mysql / oracle / sqlite (PM-C): tree-sitter ERROR ∧ lexical witness via
  //     `runTreeSitterSyntaxPass` (CK-C policy in collectTreeSitterSyntaxMarkers).
  //     The parse is main-thread WASM, so EVERY run — including the initial one —
  //     goes through the debounce, a statement-count cap (~500) backs up
  //     MAX_SOURCE_LEN, and the parse result is shared with the VQE compact
  //     affordance through `getSharedErrorRecoveryView` (never a second parse
  //     of the same buffer). One-in-flight coalescing: a recompute landing while
  //     another runs re-schedules instead of stacking parses.
  //   • pgsql stays OUT (pgls owns its diagnostics).
  // Markers are EVENTUALLY-CONSISTENT — stale results (model version changed
  // during an await) are dropped, never painted. Clones the type-check pass
  // shape (owner / debounce / MAX_SOURCE_LEN).
  useEffect(() => {
    if (!isSqlLanguage || !isEditorMounted) return;
    if (!liveSyntaxDiagnosticsEnabled || !liveDiagnosticsMasterEnabled) {
      const model = editorRef.current?.getModel();
      const monaco = monacoRef.current;
      if (monaco && model) monaco.editor.setModelMarkers(model, 'sql-syntax', []);
      return;
    }
    const monaco = monacoRef.current;
    if (!monaco) return;

    const SYNTAX_OWNER = 'sql-syntax';
    const DEBOUNCE_MS = 400;
    const MAX_SOURCE_LEN = 200_000; // skip pathologically large buffers

    const TREE_SITTER_ENGINES: readonly TreeSitterSyntaxEngineId[] = ['mysql', 'oracle', 'sqlite'];
    const asTreeSitterEngine = (id: string): TreeSitterSyntaxEngineId | null =>
      (TREE_SITTER_ENGINES as readonly string[]).includes(id) ? (id as TreeSitterSyntaxEngineId) : null;

    // E2E-only: record which model version the last syntax pass painted, so
    // the coverage harness can wait for the pass over the CURRENT buffer
    // instead of reading whatever markers happen to be there (a 0 count
    // before the debounce fires is not a clean parse).
    const recordSyntaxPass = (model: monacoEditor.ITextModel | undefined, markerCount: number): void => {
      const w = window as unknown as {
        __jamSqlE2eSyntaxPass__?: { version: number; markers: number; engine: string };
      };
      if (!('__jamSqlE2eCompletionCapture__' in window) && !('__TEST_STORES__' in window)) return;
      w.__jamSqlE2eSyntaxPass__ = {
        version: model?.getVersionId?.() ?? -1,
        markers: markerCount,
        engine: engineIdRef.current,
      };
    };

    const clearMarkers = (): void => {
      const model = editorRef.current?.getModel?.();
      if (model) monaco.editor.setModelMarkers(model, SYNTAX_OWNER, []);
      recordSyntaxPass(model ?? undefined, 0);
    };

    const paintMarkers = (
      model: monacoEditor.ITextModel,
      markers: readonly { startOffset: number; endOffset: number; message: string; code: string }[],
      engine: string,
    ): void => {
      monaco.editor.setModelMarkers(
        model,
        SYNTAX_OWNER,
        markers.map((m) => {
          const start = model.getPositionAt(m.startOffset);
          const end = model.getPositionAt(m.endOffset);
          return {
            startLineNumber: start.lineNumber,
            startColumn: start.column,
            endLineNumber: end.lineNumber,
            endColumn: end.column,
            message: m.message,
            severity: monaco.MarkerSeverity.Error,
            source: 'sql-syntax',
            code: m.code,
          };
        }),
      );
      recordSyntaxPass(model, markers.length);
      if (markers.length > 0 && !syntaxDiagAnalyticsFiredRef.current) {
        syntaxDiagAnalyticsFiredRef.current = true;
        window.electronAPI?.analytics
          ?.track('feature:syntax_diagnostics', { engine })
          .catch(() => {});
      }
    };

    // `isLive()` is false once the effect has been torn down (setting disabled /
    // connection switched). Both recompute paths check it right before painting
    // so a cold parse that resolves AFTER cleanup can't repaint (F4).
    const recomputeMssql = async (isLive: () => boolean): Promise<void> => {
      const model = editorRef.current?.getModel?.();
      if (!model) return;
      const source = model.getValue();
      if (source.length > MAX_SOURCE_LEN) { clearMarkers(); return; }

      // Both awaits below can be slow (worker round-trip; cold bundle load =
      // seconds); capture the version so a buffer edit during either gap can't
      // let stale-source markers clobber the new buffer (the edit's own
      // debounced re-run sets the correct markers).
      const versionAtStart = model.getVersionId();
      // Worker-carried ANTLR errors. `null` = no verdict (superseded by a
      // newer edit's request, or the worker path latched off) — leave the
      // currently-painted markers alone.
      const parseErrors = await getWorkerSyntaxParseErrors(source);
      if (!isLive() || parseErrors === null) return;
      if (editorRef.current?.getModel?.() !== model) return;
      if (model.getVersionId() !== versionAtStart) return;

      const markers = await buildSyntaxMarkersFromParseErrors(source, parseErrors);
      // Re-check after the (async, tree-sitter) suppression pass too.
      if (!isLive()) return;
      if (editorRef.current?.getModel?.() !== model) return;
      if (model.getVersionId() !== versionAtStart) return;
      paintMarkers(model, markers, 'mssql');
    };

    const recomputeTreeSitter = async (
      engine: TreeSitterSyntaxEngineId,
      isLive: () => boolean,
    ): Promise<void> => {
      const model = editorRef.current?.getModel?.();
      if (!model) return;
      const markers = await runTreeSitterSyntaxPass(model, engine);
      // `null` = stale (buffer changed mid-parse) — leave painted markers alone.
      if (!isLive() || markers === null) return;
      if (editorRef.current?.getModel?.() !== model) return;
      paintMarkers(model, markers, engine);
    };

    // Debounce / one-in-flight coalescing / effect-cancellation live in the
    // runner (orchestrator resolution 3 + F4). A new runner is created per
    // effect invocation so its `cancelled` flag is captured per invocation.
    const runner = createLiveSyntaxPassRunner({
      debounceMs: DEBOUNCE_MS,
      setTimeout: (fn, ms) => setTimeout(fn, ms),
      clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
      run: async (isLive) => {
        const engineId = engineIdRef.current;
        if (engineId === 'mssql') {
          await recomputeMssql(isLive);
          return;
        }
        const tsEngine = asTreeSitterEngine(engineId);
        if (tsEngine) {
          await recomputeTreeSitter(tsEngine, isLive);
          return;
        }
        clearMarkers(); // pgsql (pgls-owned) / mock / kusto
      },
    });

    const contentDisposable = editorRef.current?.getModel?.()?.onDidChangeContent(() => runner.schedule());

    // Initial pass: mssql's parse is worker-carried → safe to run immediately.
    // The tree-sitter engines parse ON the main thread → the initial pass goes
    // through the debounce too (resolution 3; don't clone the mssql behavior).
    if (engineIdRef.current === 'mssql') {
      runner.runNow();
    } else {
      runner.schedule();
    }

    return () => {
      runner.cancel();
      contentDisposable?.dispose();
      clearMarkers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditorMounted, isSqlLanguage, editorLanguage, connectionId, database, liveSyntaxDiagnosticsEnabled, liveDiagnosticsMasterEnabled]);

  // F3 — procedural cold-start warm-up (register #38). At editor mount /
  // model-attach, if the buffer HEAD opens an mssql procedural definition
  // (CREATE/ALTER PROC/FUNCTION/TRIGGER), fire a fire-and-forget scope request
  // so the parse worker's ~17 s cold ANTLR bundle load starts NOW rather than
  // racing the first completion. mssql-only: the procedural-scope worker bridge
  // serves only mssql; the other engines resolve procedural locals inline with
  // no cold-start seam. `warmUpProceduralWorker` owns all the gating —
  // opener-only, once per (documentUri, worker-generation), StrictMode-safe,
  // never throws — so this effect is a thin dispatcher. R8 (binding): this runs
  // on the MOUNT path only, never on `onDidChangeContent` or the completion
  // provider, so warm-up can never touch the keystroke path.
  // F3-debt fix: pass the model straight through — `dispatchProceduralWarmUpFromModel`
  // owns ALL model access (including `uri.toString()`) inside its own
  // try/catch, so this effect body itself can never throw synchronously into
  // React's passive-effect flush, even given a hostile/incomplete model.
  useEffect(() => {
    if (!isEditorMounted || !isSqlLanguage) return;
    if (engineIdRef.current !== 'mssql') return;
    dispatchProceduralWarmUpFromModel(editorRef.current?.getModel?.());
  }, [isEditorMounted, isSqlLanguage, editorLanguage, connectionId, database]);

  // Register the navigational-features DefinitionProvider INDEPENDENTLY of
  // the LSP handshake. PRD: Ralph/NavigationalFeatures/PRD.md § Slice 3.
  //
  // The factory is engine-agnostic. Capability slices 5–9 (table/view,
  // column, alias, CTE, routine) all resolve directly against the OE tree /
  // buffer scanners — none of them needed a per-Monaco resolver-service
  // instance, so the `getResolverService` dep that used to live here was
  // deleted (Plan 19 T9, decision 10) rather than wired to a real service.
  // Monaco's "Go to Definition" / "Peek Definition" menu entries surface
  // automatically because Monaco renders them whenever a DefinitionProvider
  // is registered for the active language.
  //
  // The provider is side-effect free (Monaco also calls it for the Ctrl/Cmd+
  // hover link probe); table/view, column and routine navigations run in the
  // renderer-wide editor opener, installed once (idempotent) and never torn
  // down by an editor unmount. `isOwnModel` keeps the other mounted editors'
  // providers from answering for this editor's model, and the ownership claim
  // keeps the renderer-wide fallback (`installUnownedModelDefinitionProvider`,
  // the one responder for Peek References previews and plain Monaco SQL
  // editors) from answering for it too — one provider per model.
  useEffect(() => {
    if (!isSqlLanguage) return;
    if (!monacoRef.current || !isEditorMounted) return;
    const monaco = monacoRef.current;

    installDefinitionNavigationOpener(monaco);
    installUnownedModelDefinitionProvider(monaco, editorLanguage);
    const getOwnModel = () => editorRef.current?.getModel?.() ?? null;
    const releaseOwnership = registerDefinitionModelOwner(getOwnModel);

    const disposable = monaco.languages.registerDefinitionProvider(
      editorLanguage,
      createDefinitionProvider({
        engineId: engineIdRef.current,
        getDocumentId: () => documentUriRef.current || null,
        getConnectionId: () => connectionIdRef.current ?? null,
        getDatabase: () => databaseRef.current ?? null,
        isOwnModel: (model) => getOwnModel() === model,
        monaco,
      }),
    );

    return () => {
      disposable.dispose();
      releaseOwnership();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditorMounted, isSqlLanguage, editorLanguage]);

  // Register the navigational-features CodeActionProvider INDEPENDENTLY of
  // the LSP handshake. PRD: Ralph/NavigationalFeatures/PRD.md § Slice 4.
  //
  // Slice 4 ships the wiring with an empty registry — `provideCodeActions`
  // returns no actions, so the lightbulb never appears, but Monaco's
  // right-click "Quick Fix…" / "Refactor…" entries surface for free as soon
  // as capability slices 10–14 register their first fix factory. The
  // registry ref is stable across renders so capability slices can register
  // at module-init time without being torn down.
  useEffect(() => {
    if (!isSqlLanguage || !intellisenseEnabled) return;
    if (!monacoRef.current || !isEditorMounted) return;
    const monaco = monacoRef.current;

    registerNavCodeActionTrackingCommand(monaco);

    const disposable = monaco.languages.registerCodeActionProvider(
      editorLanguage,
      createCodeActionProvider({
        engineId: engineIdRef.current,
        getDocumentId: () => documentUriRef.current || null,
        getConnectionId: () => connectionIdRef.current ?? null,
        getDatabase: () => databaseRef.current ?? null,
        registry: codeActionRegistryRef.current,
      }),
    );

    return () => disposable.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditorMounted, isSqlLanguage, editorLanguage, intellisenseEnabled]);

  // Move the code-action lightbulb out of the text and into the gutter lane
  // between the line numbers and the code. Monaco 0.44 anchors its bulb at
  // column 1 and, for lines without ~22px of leading indentation (i.e. most
  // SQL), relocates it to the NEIGHBOURING line — where it paints straight
  // over that line's first character. See `codeActionLightbulb.ts` for the
  // full diagnosis and for why the glyph margin is not the destination.
  useEffect(() => {
    if (!isSqlLanguage) return;
    if (!monacoRef.current || !isEditorMounted || !editorRef.current) return;
    const handle = attachCodeActionLightbulb(editorRef.current, monacoRef.current);
    return () => handle.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditorMounted, isSqlLanguage, editorLanguage]);

  // Register the navigational-features ReferenceProvider — Phase 3 Slice 24,
  // cross-object peek revision (2026-08-23).
  //
  // Surfaces Monaco's "Find All References" / "Peek References" / "Go to
  // References" menu entries (and the Shift+F12 keybinding). The provider
  // returns REAL locations pointing into synthesized read-only `jamsql-ref:`
  // models — one per referencing persisted object — so the native peek widget
  // works cross-object. `installPeekReferenceIntegration` wires the editor
  // opener (peek row → CREATE-text tab) + the read-only guard on the peek
  // preview; the extra editor action keeps the grouped ReferencesTab
  // reachable now that the provider no longer opens it as a side-effect.
  useEffect(() => {
    if (!isSqlLanguage) return;
    if (!monacoRef.current || !isEditorMounted) return;
    const monaco = monacoRef.current;

    installPeekReferenceIntegration(monaco);

    const navDeps = {
      engineId: engineIdRef.current,
      getConnectionId: () => connectionIdRef.current ?? null,
      getDatabase: () => databaseRef.current ?? null,
    };

    const disposable = monaco.languages.registerReferenceProvider(
      editorLanguage,
      createReferenceProvider({
        ...navDeps,
        monaco,
        getLanguageId: () => editorLanguage,
      }),
    );
    const actionDisposable = editorRef.current?.addAction?.(
      createFindReferencesInTabAction(navDeps),
    );

    return () => {
      disposable.dispose();
      actionDisposable?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditorMounted, isSqlLanguage, editorLanguage]);

  // Register the navigational-features RenameProvider — Phase 3 Slice 36.
  // Surfaces Monaco's "Rename Symbol" right-click menu entry + the F2
  // keybinding for cursor-on-column / cursor-on-table / cursor-on-view.
  // Dispatch opens the navigational `RenamePreviewDialog` as a side-effect
  // and returns an empty WorkspaceEdit so Monaco does NOT mutate the active
  // buffer (NAV1 — tabs are scratchpads). See renameProvider.ts.
  useEffect(() => {
    if (!isSqlLanguage) return;
    if (!monacoRef.current || !isEditorMounted) return;
    const monaco = monacoRef.current;

    const disposable = monaco.languages.registerRenameProvider(
      editorLanguage,
      createRenameProvider({
        engineId: engineIdRef.current,
        getConnectionId: () => connectionIdRef.current ?? null,
        getDatabase: () => databaseRef.current ?? null,
      }),
    );

    return () => disposable.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditorMounted, isSqlLanguage, editorLanguage]);

  // Register a SELECT * expansion hover provider INDEPENDENTLY of the LSP
  // handshake.
  //
  // The expansion tooltip is resolver-backed (the VQE's parse + live schema
  // snapshot — the list "Choose columns" writes, see selectStarHoverProvider),
  // so it does not need the SQL Tools Service LSP to be ready. Bundling it
  // with the LSP-gated hover provider above made it silently unavailable in
  // E2E test mode, where `main.ts` deliberately skips SSTS startup.
  // Decoupling mirrors the inlay hints pattern.
  useEffect(() => {
    if (!isSqlLanguage || !hoverAssistEnabled) return;
    if (!monacoRef.current || !isEditorMounted) return;
    const monaco = monacoRef.current;

    const disposable = monaco.languages.registerHoverProvider(
      editorLanguage,
      createSelectStarHoverProvider({
        isEditorModel: (model) => editorRef.current?.getModel?.() === model,
        getEngineId: () => engineIdRef.current,
        getConnectionId: () => connectionId,
        getDatabase: () => languageServiceDatabase,
      }),
    );

    return () => disposable.dispose();

  }, [isEditorMounted, isSqlLanguage, editorLanguage, connectionId, languageServiceDatabase, hoverAssistEnabled]);

  // Register a schema-aware table-hover provider INDEPENDENTLY of the LSP
  // handshake — fires for all SQL engines.
  //
  // Why bus-based: every engine cache (mssql/pgsql/mysql/oracle/sqlite)
  // already publishes a normalized `SchemaSnapshot` to the schema bus, so a
  // single bus reader covers the parity case ("hover on a table shows its
  // column list + types"). The per-engine renderer hover modules
  // (mysqlHover / oracleHover / sqliteHover) keep doing what they do best
  // through the LSP-gated path — non-table objects (sequences, packages,
  // routines), engine-specific identifier quoting, etc.
  //
  // Why always-on: the LSP-gated `provideHover` above only registers once
  // `isDocumentRegistered` is true. In E2E specs the gate frequently misses
  // the first hover trigger (SSTS / pgls re-registration timing), and SSTS
  // itself returns sparse hover for tables anyway. This provider fires the
  // moment a snapshot is on the bus, regardless of LSP state.
  //
  // Identifier dialect: `getMssqlHoverInfo` parses bare /
  // `"double-quoted"` / `[bracketed]` forms; the bracketed form is MSSQL-only
  // and is a harmless no-op on PG/MySQL/Oracle/SQLite buffers since those
  // never contain `[…]`. Backtick-quoted MySQL identifiers fall through to
  // the LSP-gated `mysqlHover` path.
  useEffect(() => {
    if (!isSqlLanguage || !hoverAssistEnabled) return;
    if (!monacoRef.current || !isEditorMounted) return;
    const monaco = monacoRef.current;

    const disposable = monaco.languages.registerHoverProvider(editorLanguage, {
      provideHover: (model: monacoEditor.ITextModel, position: monacoNs.Position) => {
        if (editorRef.current?.getModel?.() !== model) return { contents: [] };
        const connId = connectionIdRef.current ?? undefined;
        const db = databaseRef.current ?? undefined;
        if (!connId || !db) return { contents: [] };
        const snapshot = getSchemaBusSnapshot(connId, db);
        if (!snapshot) return { contents: [] };
        const local = getMssqlHoverInfo({
          text: model.getValue(),
          position: { line: position.lineNumber - 1, character: position.column - 1 },
          snapshot,
        });
        if (!local) return { contents: [] };
        return {
          contents: buildHoverContents(local.contents),
          range: local.range
            ? {
                startLineNumber: local.range.start.line + 1,
                startColumn: local.range.start.character + 1,
                endLineNumber: local.range.end.line + 1,
                endColumn: local.range.end.character + 1,
              }
            : undefined,
        };
      },
    });

    return () => disposable.dispose();
  }, [isEditorMounted, isSqlLanguage, editorLanguage, hoverAssistEnabled]);

  // Item C3 — semantic type-on-hover. Registered INDEPENDENTLY of the LSP
  // handshake (pure text + bus snapshot). Returns column data-types and
  // inferred expression result-types; returns `{ contents: [] }` for anything
  // it can't type with confidence, so Monaco falls through to the table-card
  // hover (above) and the LSP hover — they compose, never clobber. Gated by the
  // `semanticTypeHover` setting so the effect re-runs (registers/disposes) on
  // toggle.
  useEffect(() => {
    if (!isSqlLanguage || !isEditorMounted || !semanticTypeHoverEnabled || !hoverAssistEnabled) return;
    if (!monacoRef.current) return;
    const monaco = monacoRef.current;

    const disposable = monaco.languages.registerHoverProvider(editorLanguage, {
      provideHover: (model: monacoEditor.ITextModel, position: monacoNs.Position) => {
        if (editorRef.current?.getModel?.() !== model) return { contents: [] };
        const connId = connectionIdRef.current ?? undefined;
        const db = databaseRef.current ?? undefined;
        if (!connId) return { contents: [] };
        const snapshot = getSchemaBusSnapshot(connId, db);
        if (!snapshot || snapshot.tables.length === 0) return { contents: [] };

        const info = resolveSemanticHover({
          source: model.getValue(),
          offset: model.getOffsetAt(position),
          engineId: engineIdRef.current,
          snapshotTables: snapshot.tables,
          snapshotRoutines: snapshot.routines,
        });
        if (!info) return { contents: [] };

        if (!semanticHoverAnalyticsFiredRef.current) {
          semanticHoverAnalyticsFiredRef.current = true;
          window.electronAPI?.analytics
            ?.track('feature:semantic_hover', { kind: info.kind })
            .catch(() => {});
        }

        const value =
          info.kind === 'column'
            ? `**${info.columnName}** · \`${info.dataType}\` _(${info.category})_`
            : `**expression** · _(${info.category})_`;
        return {
          contents: [{ value, isTrusted: false }],
          range: {
            startLineNumber: model.getPositionAt(info.range.startOffset).lineNumber,
            startColumn: model.getPositionAt(info.range.startOffset).column,
            endLineNumber: model.getPositionAt(info.range.endOffset).lineNumber,
            endColumn: model.getPositionAt(info.range.endOffset).column,
          },
        };
      },
    });

    return () => disposable.dispose();
  }, [isEditorMounted, isSqlLanguage, editorLanguage, connectionId, database, semanticTypeHoverEnabled, hoverAssistEnabled]);

  // Register an INSERT VALUES signature-help provider INDEPENDENTLY of the LSP
  // handshake.
  //
  // This popup shows the full column list of an INSERT INTO ... VALUES (...)
  // statement and highlights the column whose value the cursor is currently
  // typing. It complements the existing inlay hints: on long single-line
  // INSERTs where Monaco's InlayHintsController truncates hints at the
  // viewport boundary, this signature-help popup is the reliable "you are
  // here" indicator because it is a floating widget, not injected text.
  //
  // The existing LSP signature-help provider (registered earlier in this file
  // for function/procedure argument hints) keeps working unchanged: this
  // provider returns null whenever the cursor is not inside an INSERT VALUES
  // tuple, so Monaco falls through to the LSP provider for `someFunc(`.
  useEffect(() => {
    if (!isSqlLanguage || !hoverAssistEnabled) return;
    if (!monacoRef.current || !isEditorMounted) return;
    const monaco = monacoRef.current;

    const disposable = monaco.languages.registerSignatureHelpProvider(editorLanguage, {
      signatureHelpTriggerCharacters: ['(', ','],
      provideSignatureHelp: async (
        model: monacoEditor.ITextModel,
        position: monacoNs.Position,
        token?: monacoNs.CancellationToken
      ) => {
        if (editorRef.current?.getModel?.() !== model) return null;
        const text = model.getValue();
        const offset = model.getOffsetAt(position);
        const result = await computeInsertSignatureHelp(
          text,
          offset,
          connectionId,
          database,
          engineIdRef.current,
        );
        if (editorRef.current?.getModel?.() !== model) return null;
        if (token?.isCancellationRequested) return null;
        if (!result || !Array.isArray(result.signatures) || result.signatures.length === 0) return null;
        return { value: result, dispose: () => {} };
      },
    });

    return () => {
      disposable.dispose();
    };
  }, [isEditorMounted, isSqlLanguage, editorLanguage, connectionId, database, hoverAssistEnabled]);

  // Register a function/procedure signature-help provider INDEPENDENTLY of
  // the LSP handshake.
  //
  // Why this exists separately from the LSP signature-help provider above:
  // SQL Tools Service 5.0.20250321.1 answers textDocument/signatureHelp with
  // `{ signatures: null }` for every input — user-defined functions,
  // built-ins, stored procedures alike — so the LSP-driven path is dormant
  // in the bundled version. This provider instead queries the database's
  // sys.parameters catalog via the `get-routine-metadata` IPC (see
  // `functionSignatureHelp.ts` + `routineParameterResolver.ts`) so users get
  // real argument hints for `{schema}.{name}(` today.
  //
  // Decoupled from the LSP effect (and its isDocumentRegistered guard) for
  // the same reason the inlay-hints provider is: this feature is
  // IPC-backed, not SSTS-backed, so it should work in any state where Monaco
  // and the editor are mounted — including MSSQL real-DB E2E, where the
  // LSP document lifecycle is entirely controlled by SSTS.
  useEffect(() => {
    if (!isSqlLanguage || !hoverAssistEnabled) return;
    if (!monacoRef.current || !isEditorMounted) return;
    const monaco = monacoRef.current;

    const disposable = monaco.languages.registerSignatureHelpProvider(editorLanguage, {
      signatureHelpTriggerCharacters: ['(', ','],
      provideSignatureHelp: async (
        model: monacoEditor.ITextModel,
        position: monacoNs.Position,
        token?: monacoNs.CancellationToken
      ) => {
        if (editorRef.current?.getModel?.() !== model) return null;
        const text = model.getValue();
        const offset = model.getOffsetAt(position);
        const result = await computeFunctionSignatureHelp(
          text,
          offset,
          connectionId,
          database,
          engineIdRef.current,
        );
        if (editorRef.current?.getModel?.() !== model) return null;
        if (token?.isCancellationRequested) return null;
        if (!result || !Array.isArray(result.signatures) || result.signatures.length === 0) return null;
        return { value: result, dispose: () => {} };
      },
    });

    return () => {
      disposable.dispose();
    };
  }, [isEditorMounted, isSqlLanguage, editorLanguage, connectionId, database, hoverAssistEnabled]);

  // Subscribe to IntelliSense status changes
  useEffect(() => {
    if (!documentUri || !languageServiceProvider.onIntelliSenseStatus) {
      // No document URI yet, report unavailable
      onIntelliSenseStatusChange?.('unavailable');
      return;
    }

    // Get initial status
    const initialStatus = languageServiceProvider.getIntelliSenseStatus?.(documentUri) || 'unavailable';
    onIntelliSenseStatusChange?.(initialStatus);

    // Subscribe to status changes
    const unsubscribe = languageServiceProvider.onIntelliSenseStatus((payload) => {
      if (payload.uri === documentUri) {
        onIntelliSenseStatusChange?.(payload.status, payload.message);

        // SSTS can report `intelliSenseReady` (status → 'ready') a beat before
        // it has actually bound the schema, so the first completion can come
        // back keyword-only. The language-service layer self-heals (rebuild /
        // reconnect) and re-emits 'ready' once the schema is genuinely bound —
        // but Monaco has already cached that keyword-only list and won't
        // re-query on its own, so an open suggest widget stays stale until the
        // user types another character. When 'ready' arrives and a suggest
        // widget is currently open, re-run the completion provider so the
        // stale keyword list is replaced with schema-aware items. Gated on an
        // already-open widget so we never pop the suggest UI unsolicited —
        // and re-run through the suggest session (`rerunSuggestSession`),
        // since `editor.action.triggerSuggest` is disabled while the list is
        // visible and used to make this a silent no-op.
        // `partial` counts as ready here (S7 item 5): schema-aware completion
        // is bound, just missing the artefacts a row cap clipped — the stale
        // keyword-only list in an open widget must still be replaced.
        if ((payload.status === 'ready' || payload.status === 'partial') && editorRef.current) {
          if (isSuggestWidgetVisible(editorRef.current)) {
            rerunSuggestSession(editorRef.current);
          }
        }
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, [documentUri, languageServiceProvider, onIntelliSenseStatusChange]);

  // INT-12 — re-run the suggest widget on the cold→warm schema transition.
  // When the user triggers completion before the schema snapshot is published
  // the resolver can only return keyword-level suggestions. The fire-and-
  // forget warm publishes via the schema bus shortly after; this re-runs
  // suggest once (only the cold→warm transition, only while focused, only
  // while a list is already open — same gate as the SSTS `ready` path above,
  // so a freshly focused tab never pops the list unasked) so the schema-aware
  // candidates replace the cold list. All logic is in the unit-tested helper;
  // this is a thin wiring shell. We bind to the exact (connectionId, database)
  // the completion provider reads (connectionIdRef / databaseRef) so the bus
  // key matches.
  useEffect(() => {
    if (!isEditorMounted || !connectionId) return;
    return makeWarmSuggestRetrigger({
      subscribe: subscribeSchemaBus,
      connectionId,
      database: database ?? undefined,
      getEditorHandle: () => editorRef.current,
      isSuggestWidgetOpen: () => isSuggestWidgetVisible(editorRef.current),
      rerunSuggest: () => {
        rerunSuggestSession(editorRef.current);
      },
      isSuppressed: () => !isAutoSuggestEnabled(useAppStore.getState().intellisenseSettings),
    });
  }, [isEditorMounted, connectionId, database]);

  // Keep the Monaco model's language in sync with `editorLanguage` AFTER mount.
  //
  // `@monaco-editor/react` reads `defaultLanguage` only at model creation, and
  // `handleEditorDidMount` runs its kusto upgrade exactly once — so a tab that
  // mounts before its engine resolves (activeConnections still empty →
  // QueryEditorTab falls back to engineId 'mssql' → editorLanguage 'sql') would
  // otherwise stay on SQL tokenization/IntelliSense forever once the real engine
  // arrives and `editorLanguage` recomputes. This effect reacts to that
  // transition in both directions: it switches the model language (loading
  // monaco-kusto's AMD contribution first for 'kusto') and re-wires the
  // language-service document so the newly-resolved provider's schema feed
  // starts (kusto) or the SQL LSP path is restored (sql/plsql).
  //
  // The document is ALSO re-wired when the resolved engine changes without a
  // language change — mssql, pgsql, mysql and sqlite all edit as 'sql', so a
  // tab switched between them never trips the language check, and its document
  // stayed registered (and schema-loaded) with the previous engine's provider.
  // Tabs whose engine never changes never advance past the first pass below,
  // so their behavior is unchanged.
  const appliedLanguageRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isEditorMounted) return;
    const monaco = monacoRef.current;
    const editor = editorRef.current;
    if (!monaco || !editor) return;

    const applyModelLanguage = () => {
      const model = editor.getModel();
      if (!model || model.isDisposed?.()) return;
      if (editorLanguage === 'kusto') {
        if (model.getLanguageId?.() === 'kusto') return;
        void ensureKustoLanguageLoaded()
          .then(() => {
            const m = editor.getModel();
            if (m && !m.isDisposed?.() && m.getLanguageId?.() !== 'kusto') {
              monaco.editor.setModelLanguage(m, 'kusto');
            }
          })
          .catch(() => {});
        return;
      }
      if (model.getLanguageId?.() === editorLanguage) return;
      monaco.editor.setModelLanguage(model, editorLanguage);
    };

    const previous = appliedLanguageRef.current;
    appliedLanguageRef.current = editorLanguage;
    const languageChanged = previous !== null && previous !== editorLanguage;
    // The setup / re-register effects run earlier in the same commit and bind
    // the document to THIS commit's provider, so a mismatch here means the
    // engine changed under an already-bound document.
    const boundProvider = documentProviderRef.current;
    const providerChanged = boundProvider !== null && boundProvider !== languageServiceProvider;

    // First post-mount pass: the mount path + initial-document-setup effect own
    // the initial wiring. Only reconcile the model language — this covers the
    // case where `editorLanguage` changed during Monaco's async mount window
    // (the onMount closure captured a stale value).
    if (previous === null || languageChanged) applyModelLanguage();
    if (!languageChanged && !providerChanged) return;

    // Re-wire the language-service document for the newly-resolved provider.
    // Cancel any registration still scheduled, close the document through the
    // provider that holds it (`boundProvider` — the pre-switch engine's), then
    // reset the setup guards + bump the nonce so the initial-document-setup
    // effect re-runs on the next render and registers + connects the document
    // with this render's provider.
    if (documentSetupTimerRef.current !== null) {
      clearTimeout(documentSetupTimerRef.current);
      documentSetupTimerRef.current = null;
    }
    // A re-register armed in the same commit (connection and language
    // switched together): let it die too, the setup effect below registers
    // the document again once the model language has been reconciled.
    if (registerAndConnectTimerRef.current !== null) {
      clearTimeout(registerAndConnectTimerRef.current);
      registerAndConnectTimerRef.current = null;
    }
    const oldUri = registeredUriRef.current;
    if (oldUri) {
      const oldConnId = getConnectionIdFromDocumentUri(oldUri);
      if (oldConnId) unregisterDocumentForConnection(oldConnId, oldUri);
      (boundProvider ?? languageServiceProvider).closeDocument(oldUri).catch(() => {});
    }
    registeredUriRef.current = null;
    documentProviderRef.current = null;
    previousConnectionId.current = null;
    previousDatabase.current = null;
    initialDocumentSetupDone.current = false;
    setIsDocumentRegistered(false);
    setDocumentUri('');
    setDocumentSetupNonce((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorLanguage, isEditorMounted, languageServiceProvider]);

  const handleEditorDidMount = (editor: IStandaloneCodeEditor, monaco: Monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Keep the word-based-suggestion scope in step with the caret. Cursor
    // change fires before the suggest model gathers its providers, so the
    // option is already correct by the time the widget opens.
    const caretTracked = editor as IStandaloneCodeEditor & {
      onDidChangeCursorPosition?: (listener: () => void) => { dispose: () => void } | undefined;
      onDidFocusEditorText?: (listener: () => void) => { dispose: () => void } | undefined;
    };
    caretTracked.onDidChangeCursorPosition?.(() => syncWordBasedSuggestions(editor));
    // The option lands in the standalone configuration service that every
    // editor shares, so another editor's mount or caret move can write it last;
    // re-scope on focus so the editor being typed in owns it.
    caretTracked.onDidFocusEditorText?.(() => syncWordBasedSuggestions(editor));
    syncWordBasedSuggestions(editor);

    // Kusto's AMD contribution registers the `'kusto'` Monaco language
    // asynchronously, but `handleBeforeMount` (below) is synchronous and the
    // `<Editor>` model is created uncontrolled from `defaultLanguage` at
    // mount time — so a kusto model can't mount directly as `'kusto'`. It
    // mounts as `'plaintext'` instead (see the JSX below) and is switched to
    // `'kusto'` here once the contribution has loaded, which is what binds
    // monaco-kusto's tokenizer + completion/hover/worker providers to this
    // model. Schema/worker errors surface via the Task 4.3 language-service
    // provider's IntelliSense status, not here.
    if (editorLanguage === 'kusto') {
      void ensureKustoLanguageLoaded()
        .then(() => {
          const model = editor.getModel();
          if (model && !model.isDisposed()) monaco.editor.setModelLanguage(model, 'kusto');
        })
        .catch(() => {});
    }

    // Race fix: if a parent issued an imperative `setValue` (e.g. async
    // script-as-CREATE completing) BEFORE Monaco's lazy chunk finished
    // loading, that call no-oped because editorRef.current was null. The
    // parent typically also pushes the new value into local React state,
    // which flows back here as a `value` prop change — but Monaco was
    // initialised with a stale `defaultValue` and we never reconcile after
    // mount in uncontrolled mode. Resync here, once, before exposing the
    // editor as mounted.
    const latest = latestValueRef.current;
    const mounted = readModelTextSafe(editor.getModel());
    if (typeof latest === 'string' && mounted !== null && latest !== mounted) {
      editor.setValue(latest);
    }

    // Content changes are subscribed here rather than through the wrapper's
    // `onChange` prop: that prop calls `editor.getValue()` unguarded on every
    // edit, which throws for good once a model is past Monaco's 256M-char
    // heap threshold. `readModelTextSafe` turns that into a skipped update.
    // Installed AFTER the resync above, matching the wrapper (which subscribed
    // in an effect after onMount) so the mount-time `setValue` never reaches
    // the parent as a user edit.
    contentChangeDisposableRef.current?.dispose?.();
    contentChangeDisposableRef.current = editor.onDidChangeModelContent(() => {
      const text = readModelTextSafe(editor.getModel());
      if (text === null) return;
      handleEditorChangeRef.current(text);
    });

    // Refuse pastes / text drops that would exceed the editor text limit
    // before Monaco inserts them (a 256M-char document cannot be read back).
    pasteGuardDisposeRef.current?.();
    pasteGuardDisposeRef.current = installOversizedPasteGuard(editor, (details) => {
      toast.error({
        title: details.source === 'drop' ? 'Dropped text is too large for the editor' : 'Paste is too large for the editor',
        description: details.message,
      });
      window.electronAPI?.analytics
        ?.track('feature:editor_paste_blocked_too_large', { source: details.source, sizeBucket: details.sizeBucket })
        .catch(() => {});
    });

    setIsEditorMounted(true);

    // Typing-latency telemetry (feature:editor_input_latency): focus/blur
    // scope the page-global Event Timing observer to this editor, onKeyDown
    // is the keystroke denominator — its event timeStamp is what the delayed
    // Event Timing entry is matched back to. Aggregated in
    // editorInputLatency.ts — never a per-keystroke event, never the typed text.
    try {
      editor.onDidFocusEditorText(() => {
        noteKeybindingTargetFocus(editor);
        hasInputLatencyFocusRef.current = true;
        inputLatencyOwnerRef.current = notifyEditorFocus(
          telemetryEngineRef.current,
          editor.getModel()?.getLineCount() ?? 0,
        );
      });
      editor.onDidBlurEditorText(() => {
        hasInputLatencyFocusRef.current = false;
        if (inputLatencyOwnerRef.current) notifyEditorBlur(inputLatencyOwnerRef.current);
      });
      editor.onKeyDown((e) => recordKeystroke(e.browserEvent.timeStamp));
    } catch {
      // Telemetry must never break the editor.
    }

    try {
      // Label carries the registry chord and follows the effective keymap.
      registerFormatSqlContextAction(editor, (ed: monacoEditor.ICodeEditor) => {
        const currentValue = readModelTextSafe(ed.getModel());
        if (currentValue === null) return;
        void formatSql(currentValue, engineIdRef.current).then((formatted) => {
          applyFormattedSql(ed, formatted);
        });
      });
    } catch {
      // Silent fail
    }

    // Selection changes (for AI workspace selection sync)
    try {
      selectionDisposableRef.current?.dispose?.();

      const emitSelection = () => {
        const callback = onSelectionChangeRef.current;
        if (!callback) return;

        const selection = editor.getSelection?.();
        const model = editor.getModel?.();
        const isEmpty =
          !selection ||
          (typeof selection.isEmpty === 'function' ? selection.isEmpty() : false);

        const range = isEmpty
          ? null
          : {
              startLineNumber: selection.startLineNumber,
              startColumn: selection.startColumn,
              endLineNumber: selection.endLineNumber,
              endColumn: selection.endColumn,
            };

        const selectedText =
          !isEmpty && model?.getValueInRange ? model.getValueInRange(selection) : '';

        // Cursor position is resolved from the selection's end (Monaco places
        // the caret at the active side of the selection). When the model is
        // available we also expose the absolute character offset so callers
        // can map the cursor to a parsed-statement span without duplicating
        // the line-tracking logic.
        let cursorPosition: { lineNumber: number; column: number; offset: number } | null = null;
        if (selection && model && typeof model.getOffsetAt === 'function') {
          try {
            const lineNumber = selection.positionLineNumber ?? selection.endLineNumber;
            const column = selection.positionColumn ?? selection.endColumn;
            const offset = model.getOffsetAt({ lineNumber, column });
            cursorPosition = { lineNumber, column, offset };
          } catch {
            cursorPosition = null;
          }
        }

        callback({ range, selectedText, cursorPosition });
      };

      // Trailing debounce (see SELECTION_EMIT_DEBOUNCE_MS): the raw Monaco
      // event only schedules — no payload work, no React state — and the
      // final event of a burst always lands, so settled state is correct.
      const scheduleEmitSelection = () => {
        if (selectionEmitTimerRef.current !== null) clearTimeout(selectionEmitTimerRef.current);
        selectionEmitTimerRef.current = setTimeout(() => {
          selectionEmitTimerRef.current = null;
          emitSelection();
        }, SELECTION_EMIT_DEBOUNCE_MS);
      };

      const disposable = (editor as any).onDidChangeCursorSelection?.(scheduleEmitSelection);
      if (disposable && typeof disposable.dispose === 'function') {
        // Wrapped so disposal (unmount or listener re-install) also cancels a
        // pending trailing emit — it would otherwise fire into a dead editor.
        selectionDisposableRef.current = {
          dispose: () => {
            if (selectionEmitTimerRef.current !== null) {
              clearTimeout(selectionEmitTimerRef.current);
              selectionEmitTimerRef.current = null;
            }
            disposable.dispose();
          },
        };
      }

      emitSelection();
    } catch {
      // Silent fail
    }
  };

  const handleEditorChange = (newValue: string | undefined) => {
    const text = newValue || '';
    // Trailing debounce: schedule a single LSP `updateDocument` to fire
    // 250ms after the LAST keystroke. While the user is typing fast, every
    // keystroke just resets the timer — no IPC, no response handlers, no
    // synchronous work mid-burst. The LSP gets one update with the final
    // buffer when typing pauses, which is exactly when its result (e.g.
    // diagnostics, semantic info) is useful.
    if (documentUri && isDocumentRegistered) {
      if (lspUpdateTimerRef.current !== null) clearTimeout(lspUpdateTimerRef.current);
      lspUpdateTimerRef.current = setTimeout(() => {
        lspUpdateTimerRef.current = null;
        // Only while this document is still the registered one, and through
        // the provider holding it: an engine switch inside the debounce window
        // re-wires the document, and the new engine's provider must not be
        // handed the old URI.
        const provider = documentProviderRef.current;
        if (!provider || registeredUriRef.current !== documentUri) return;
        const currentText = readModelTextSafe(editorRef.current?.getModel()) ?? text;
        provider.updateDocument(documentUri, currentText).catch(() => {});
      }, 250);
    }
    if (onChange) {
      onChange(text);
    }
  };
  useLayoutEffect(() => {
    handleEditorChangeRef.current = handleEditorChange;
  });

  const handleFormatSqlKeybinding = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return false;
    const currentValue = readModelTextSafe(editor.getModel());
    if (currentValue === null) return false;
    void formatSql(currentValue, engineIdRef.current).then((formatted) => {
      applyFormattedSql(editorRef.current, formatted);
    });
    return true;
  }, []);
  useFormatSqlKeybinding({
    editorRef,
    rootRef,
    onFormat: handleFormatSqlKeybinding,
  });

  const handleBeforeMount = (monaco: Monaco) => {
    monacoRef.current = monaco;
    ensureJamMonacoThemes(monaco);
    registerSqlLanguage(monaco);
    registerPlSqlLanguage(monaco);
  };

  return (
    <div ref={rootRef} className={`w-full h-full flex flex-col ${className}`}>
      <Editor
        path={modelPathRef.current}
        height={height}
        defaultLanguage={editorLanguage === 'kusto' ? 'plaintext' : editorLanguage}
        defaultValue={value}
        beforeMount={handleBeforeMount}
        onMount={handleEditorDidMount}
        options={{
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap,
          automaticLayout: true,
          fontFamily: MONO_FONT_STACK,
          fontSize: 14,
          lineNumbers: "on",
          tabSize,
          contextmenu: true,
          folding: true,
          renderLineHighlight: "all",
          renderWhitespace: "none",
          renderControlCharacters: false,
          padding: { top: 8, bottom: 8 },
          quickSuggestions: autoSuggestEnabled,
          suggestOnTriggerCharacters: autoSuggestEnabled,
          // IntelliSense master switch — hover widget and parameter hints go
          // quiet too, and `syncWordBasedSuggestions` turns Monaco's own
          // buffer-word scrape off (never set here: this object is re-applied
          // on every render), so "off" means no popup of any kind (Ctrl+Space
          // included).
          hover: { enabled: intellisenseEnabled },
          parameterHints: { enabled: intellisenseEnabled },
          // G5 (Ralph/IntellisenseEngine/PRD.md): Enter only accepts a
          // suggestion when it makes a textual change; an exact-match
          // no-op accept lets Enter fall through as a newline. Tab always
          // accepts. Addresses "autocomplete is too intrusive" feedback.
          acceptSuggestionOnEnter: 'smart',
          fixedOverflowWidgets: true,
          inlayHints: { enabled: intellisenseEnabled ? 'on' : 'off' },
          guides: { indentation: false, highlightActiveIndentation: true, bracketPairs: true }
        }}
        theme={darkMode ? "vs-dark" : "vs"}
        className="monaco-editor-container"
      />
    </div>
  );
});

// Memoised so an unrelated re-render in the parent (workspace store ticks,
// streaming progress, AI sidebar toggles, connection-state changes) does NOT
// flow into Monaco's wrapper. Combined with the uncontrolled editor above,
// this is what keeps typing off the React render path.
export const SqlMonaco = memo(SqlMonacoInner);
